// components/LibraryViewer.tsx - Sliding Sidebar with Real Thumbnails
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import RenameDialog from './RenameDialog';
import { Play, Clock, Video, Share2, Youtube, List, GraduationCap, CheckCircle, Library as LibraryIcon, FolderOpen, Plus, Search, MoreVertical, SlidersHorizontal, X, GripVertical, ArrowUp, ArrowDown, Trash2, MinusCircle, Pencil } from 'lucide-react';
import {
  clearLibraryProjectShare,
  deleteLibraryProject,
  getLibraryProjectMediaAvailability,
  getLibraryProjectMembershipKey,
  getLibraryProjectSize,
  getLibraryProjectStoragePath,
  getLibraryProjectThumbnailPath,
  getLibraryProjectVideoUrl,
  getLibraryProjectYouTubeThumbnailUrl,
  isYouTubeLibraryProject,
  libraryProjectConsumesUploadSlot,
  listLibraryProjects,
  markLibraryProjectShared,
  renameLibraryProject,
  updateLibraryProjectLearningStatus
} from './services/videoLibrary';
import {
  addLibraryProjectToPlaylist,
  createLibraryProjectPlaylistMembership,
  createPlaylist,
  deletePlaylist,
  listPlaylistMembershipsForPlaylists,
  listPlaylists,
  normalizePlaylistTitle,
  playlistMembershipItemKey,
  playlistMembershipMatchesLibraryProject,
  renamePlaylist,
  reorderPlaylistMemberships,
  removeLibraryProjectFromAllPlaylists,
  removeLibraryProjectFromPlaylist
} from './services/playlistService';
import { decrementLibraryCount } from './services/authService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import type {
  LibraryPlaylist,
  LibraryPlaylistMembership,
  NormalizedLibraryProject,
  SharedSession,
  VideoLearningStatus
} from './types';
import type { UserProfile } from './services/authService';
import { db } from '../firebase/config';
import { VideoThumbnailService } from './services/videoThumbnailService';
import LibraryPlaylistPicker from './LibraryPlaylistPicker';
import { getCurrentPlatformCapabilities } from '../platform/platformCapabilities';

import { Eye, Calendar, AlertCircle } from 'lucide-react';
import { 
  calculateViewsRemaining,
  calculateDaysRemaining,
  formatTimeRemaining,
  getShareStatus 
} from '../utils/sharingHelpers';

// Share UI components
import { 
  getActiveShareForVideo, 
  deactivateShare,
  createSharedSession 
} from './services/sharedSessionService';

interface LibraryViewerProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadVideo: (videoMeta: NormalizedLibraryProject, videoUrl: string) => void;
  userProfile: UserProfile | null;
  refreshProfile: () => Promise<void>;
}

interface LibraryActionToast {
  message: string;
  undo: () => void | Promise<void>;
}

const formatDate = (date: any): string => {
  if (!date) return 'Unknown';
  const dateObj = date.toDate ? date.toDate() : new Date(date);
  const now = new Date();
  const diff = now.getTime() - dateObj.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return dateObj.toLocaleDateString();
};

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 MB';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const generatePlaceholderThumbnail = (title: string): string => {
  const firstLetter = title.charAt(0).toUpperCase();
  const colors = ['667', '456', '234', '789', '345', '567'];
  const randomColor = colors[Math.floor(Math.random() * colors.length)];
  
  return `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="320" height="180"%3E%3Crect fill="%23${randomColor}" width="320" height="180"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23fff" font-size="64" font-weight="bold"%3E${firstLetter}%3C/text%3E%3C/svg%3E`;
};

/** Grid-template for a playlist cover collage of `count` thumbnails (1-4). */
const getPlaylistCoverGridTemplate = (count: number): React.CSSProperties => {
  if (count <= 1) return { gridTemplateColumns: '1fr', gridTemplateRows: '1fr' };
  if (count === 2) return { gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr' };
  return { gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' };
};

/** Grid placement for one thumbnail within the collage (the 3-item layout gives the last tile a full-width row). */
const getPlaylistCoverGridItemStyle = (count: number, index: number): React.CSSProperties => (
  count === 3 && index === 2 ? { gridColumn: '1 / span 2' } : {}
);

const toCacheVersion = (value: any): string => {
  if (!value) return '';
  if (typeof value?.toMillis === 'function') {
    return String(value.toMillis());
  }
  if (value instanceof Date) {
    return String(value.getTime());
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return String(parsed.getTime());
    }
  }
  return '';
};

type LibrarySourceFilter = 'all' | 'youtube' | 'uploaded' | 'local-device';
type LibraryStatusFilter = 'all' | 'unassigned' | VideoLearningStatus;
type LibrarySurface = 'library' | 'playlists';
type LibrarySelectionKind = 'library-videos' | 'playlist-videos' | 'playlists';

interface LibrarySelectionState {
  kind: LibrarySelectionKind;
  playlistId: string | null;
  ids: string[];
}

interface LibraryLongPressSelection {
  kind: LibrarySelectionKind;
  id: string;
  startX: number;
  startY: number;
}

interface LibraryLocation {
  surface: LibrarySurface;
  playlistId: string | null;
}

const LIBRARY_LOCATION_STORAGE_KEY_PREFIX = 'croop.libraryLocation';
const DEFAULT_LIBRARY_LOCATION: LibraryLocation = { surface: 'library', playlistId: null };

const getLibraryLocationStorageKey = (uid: string | null | undefined): string =>
  `${LIBRARY_LOCATION_STORAGE_KEY_PREFIX}.${uid || 'anonymous'}`;

/** Restores the tab/playlist the user last had open, so reopening the library doesn't reset to "All videos". */
const readStoredLibraryLocation = (uid: string | null | undefined): LibraryLocation => {
  try {
    const raw = window.localStorage.getItem(getLibraryLocationStorageKey(uid));
    if (!raw) return DEFAULT_LIBRARY_LOCATION;
    const parsed = JSON.parse(raw) as Partial<LibraryLocation>;
    if (parsed.surface !== 'library' && parsed.surface !== 'playlists') return DEFAULT_LIBRARY_LOCATION;
    const playlistId = parsed.surface === 'playlists' && typeof parsed.playlistId === 'string'
      ? parsed.playlistId
      : null;
    return { surface: parsed.surface, playlistId };
  } catch {
    return DEFAULT_LIBRARY_LOCATION;
  }
};

const writeStoredLibraryLocation = (uid: string | null | undefined, location: LibraryLocation) => {
  try {
    window.localStorage.setItem(getLibraryLocationStorageKey(uid), JSON.stringify(location));
  } catch {
    // Storage can be unavailable (private mode, quota); remembering the location is best-effort.
  }
};

const LONG_PRESS_SELECTION_DELAY_MS = 520;
const LONG_PRESS_SELECTION_MOVE_TOLERANCE_PX = 8;
const PLAYLIST_COVER_GRID_LIMIT = 4;
const LIBRARY_HEADER_CONTROL_SIZE = '2.3125rem';
const LIBRARY_HEADER_TAB_HEIGHT = '2.1875rem';
const LIBRARY_HEADER_SEARCH_WIDTH = '100%';
const LIBRARY_HEADER_SEARCH_MIN_WIDTH = '0';

const LEARNING_STATUS_SEQUENCE: Array<VideoLearningStatus | undefined> = [
  undefined,
  'queued',
  'learning',
  'learned'
];

const LEARNING_STATUS_FILTER_OPTIONS: Array<{
  value: VideoLearningStatus;
  label: string;
  ariaLabel: string;
}> = [
  { value: 'queued', label: 'Queued', ariaLabel: 'Show queued library items' },
  { value: 'learning', label: 'Learning', ariaLabel: 'Show learning library items' },
  { value: 'learned', label: 'Learned', ariaLabel: 'Show learned library items' }
];

const LEARNING_STATUS_LABELS: Record<VideoLearningStatus, string> = {
  queued: 'queued',
  learning: 'learning',
  learned: 'learned'
};

const SOURCE_FILTER_OPTIONS: Array<{
  value: LibrarySourceFilter;
  label: string;
  ariaLabel: string;
}> = [
  { value: 'youtube', label: 'YTube', ariaLabel: 'Show YouTube-backed projects' },
  { value: 'uploaded', label: 'Uploaded', ariaLabel: 'Show uploaded videos and projects' },
  { value: 'local-device', label: 'Local', ariaLabel: 'Show device-local videos' }
];

const matchesLibrarySourceFilter = (
  item: NormalizedLibraryProject,
  sourceFilter: LibrarySourceFilter
): boolean => {
  if (sourceFilter === 'all') return true;
  if (sourceFilter === 'youtube') return isYouTubeLibraryProject(item);
  if (sourceFilter === 'uploaded') return libraryProjectConsumesUploadSlot(item);
  return getLibraryProjectMediaAvailability(item).status === 'device-local';
};

const isLocalLibraryProject = (project: NormalizedLibraryProject): boolean => (
  project.persistence === 'local'
);

const matchesLibraryStatusFilter = (
  item: NormalizedLibraryProject,
  statusFilter: LibraryStatusFilter
): boolean => {
  if (statusFilter === 'all') return true;
  if (statusFilter === 'unassigned') return item.learningStatus === undefined;
  return item.learningStatus === statusFilter;
};

const matchesLibraryTitleSearch = (
  item: NormalizedLibraryProject,
  normalizedSearchQuery: string,
  playlistTitles: string[] = []
): boolean => {
  if (!normalizedSearchQuery) return true;
  return item.title.toLocaleLowerCase().includes(normalizedSearchQuery) ||
    playlistTitles.some(title => title.toLocaleLowerCase().includes(normalizedSearchQuery));
};

const comparePlaylistMemberships = (
  first: LibraryPlaylistMembership,
  second: LibraryPlaylistMembership
): number => {
  const firstPosition = Number.isFinite(first.position) ? first.position : Number.MAX_SAFE_INTEGER;
  const secondPosition = Number.isFinite(second.position) ? second.position : Number.MAX_SAFE_INTEGER;
  return firstPosition - secondPosition || first.id.localeCompare(second.id);
};

const getNextLearningStatus = (
  status: VideoLearningStatus | undefined
): VideoLearningStatus | undefined => {
  const currentIndex = LEARNING_STATUS_SEQUENCE.indexOf(status);
  const safeIndex = currentIndex === -1 ? 0 : currentIndex;
  return LEARNING_STATUS_SEQUENCE[(safeIndex + 1) % LEARNING_STATUS_SEQUENCE.length];
};

const getLearningStatusLabel = (status: VideoLearningStatus | undefined): string => {
  return status ? LEARNING_STATUS_LABELS[status] : 'unassigned';
};

const renderLearningStatusIcon = (status: VideoLearningStatus | undefined) => {
  if (status === 'queued') return <List size={14} />;
  if (status === 'learning') return <GraduationCap size={15} />;
  if (status === 'learned') return <CheckCircle size={14} />;
  return <MinusCircle size={14} />;
};

const getLearningStatusFilterColor = (status: VideoLearningStatus): string => {
  if (status === 'queued') return '#93c5fd';
  if (status === 'learning') return '#fbbf24';
  return '#86efac';
};

const formatProgressLaneAriaLabel = (
  reachedCount: number,
  totalCount: number,
  singularNoun: string
): string => {
  const pluralNoun = `${singularNoun}s`;
  if (totalCount === 0) return `0 of 0 ${pluralNoun}`;

  const noun = totalCount === 1 ? singularNoun : pluralNoun;
  return `${reachedCount} of ${totalCount} ${noun}`;
};

const shouldIgnoreSelectionGesture = (target: EventTarget | null): boolean => {
  const targetElement = target as Element | null;
  return Boolean(
    targetElement?.closest?.('button, input, textarea, select, a, [role="menu"], [role="dialog"]')
  );
};

const getPlaylistErrorMessage = (error: unknown, fallback: string): string => {
  const message = error instanceof Error ? error.message : String(error);
  if (message.toLowerCase().includes('missing or insufficient permissions')) {
    return `${fallback}. Firestore rules need the owner-only users/{uid}/playlists rule deployed.`;
  }
  return message || fallback;
};

const getPlaylistMembershipErrorMessage = (error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error);
  if (message.toLowerCase().includes('missing or insufficient permissions')) {
    return 'Playlist features are temporarily unavailable. Deploy the owner-only Firestore rules for users/{uid}/playlists and users/{uid}/playlists/{playlistId}/items/{itemId}. Your library items are still available.';
  }
  return `Playlist memberships could not be loaded. Your library items are still available.${message ? ` Details: ${message}` : ''}`;
};

const menuItemStyle: React.CSSProperties = {
  width: '100%',
  display: 'block',
  padding: '0.45rem 0.5rem',
  color: '#d1d5db',
  background: 'transparent',
  border: 'none',
  borderRadius: '0.3rem',
  cursor: 'pointer',
  textAlign: 'left',
  fontSize: '0.75rem'
};

const toolbarIconButtonStyle: React.CSSProperties = {
  width: LIBRARY_HEADER_CONTROL_SIZE,
  height: LIBRARY_HEADER_CONTROL_SIZE,
  flex: `0 0 ${LIBRARY_HEADER_CONTROL_SIZE}`,
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#cbd5e1',
  backgroundColor: 'transparent',
  border: '1px solid transparent',
  borderRadius: '0.5rem',
  cursor: 'pointer'
};

const describeBulkFailure = (
  verb: string,
  succeededCount: number,
  totalCount: number,
  failedItems: Array<{ title: string; error: unknown }>
): string => {
  const firstFailure = failedItems[0];
  const message = firstFailure?.error instanceof Error
    ? firstFailure.error.message
    : String(firstFailure?.error || 'Unknown error');
  return `${verb} ${succeededCount} of ${totalCount}. ${failedItems.length} failed. ${firstFailure?.title || 'Item'}: ${message}`;
};

const LibraryViewer: React.FC<LibraryViewerProps> = ({
  isOpen,
  onClose,
  onLoadVideo,
  userProfile,
  refreshProfile
}) => {
  const sharingEnabled = getCurrentPlatformCapabilities().canExposeSharing;
  const [videos, setVideos] = useState<NormalizedLibraryProject[]>([]);
  const [playlists, setPlaylists] = useState<LibraryPlaylist[]>([]);
  const [playlistMemberships, setPlaylistMemberships] = useState<
    Record<string, LibraryPlaylistMembership[]>
  >({});
  const [initialLibraryLocation] = useState(() => readStoredLibraryLocation(userProfile?.uid));
  const [activeSurface, setActiveSurface] = useState<LibrarySurface>(initialLibraryLocation.surface);
  const [activePlaylistId, setActivePlaylistId] = useState<string | null>(initialLibraryLocation.playlistId);
  const [playlistsLoaded, setPlaylistsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [hoveredVideo, setHoveredVideo] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [editingPlaylistId, setEditingPlaylistId] = useState<string | null>(null);
  const [newPlaylistTitle, setNewPlaylistTitle] = useState('');
  const [renamingPlaylistId, setRenamingPlaylistId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<LibrarySourceFilter>('all');
  const [statusFilter, setStatusFilter] = useState<LibraryStatusFilter>('all');
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [updatingPlaylistId, setUpdatingPlaylistId] = useState<string | null>(null);
  const [reorderingPlaylist, setReorderingPlaylist] = useState(false);
  const [draggedPlaylistItemKey, setDraggedPlaylistItemKey] = useState<string | null>(null);
  const [dragOverPlaylistItemKey, setDragOverPlaylistItemKey] = useState<string | null>(null);
  const [selection, setSelection] = useState<LibrarySelectionState | null>(null);
  const [bulkActionInProgress, setBulkActionInProgress] = useState(false);
  const [openPlaylistPickerId, setOpenPlaylistPickerId] = useState<string | null>(null);
  const [openVideoMenuId, setOpenVideoMenuId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [actionToast, setActionToast] = useState<LibraryActionToast | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const longPressTimerRef = useRef<number | null>(null);
  const longPressSelectionRef = useRef<LibraryLongPressSelection | null>(null);
  const suppressNextCardClickRef = useRef(false);
  const [creatingPlaylist, setCreatingPlaylist] = useState(false);
  const [viewportProgress, setViewportProgress] = useState({ reached: 0, total: 0 });
  
  // Real thumbnail URLs from Firebase Storage
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  const [thumbnailsLoading, setThumbnailsLoading] = useState<Record<string, boolean>>({});
  
  // Share Management State
  const [shareDataMap, setShareDataMap] = useState<Record<string, SharedSession | null>>({});
  const [loadingShares, setLoadingShares] = useState<Record<string, boolean>>({});

  const clearLongPressSelectionTimer = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    longPressSelectionRef.current = null;
  }, []);

  const updateViewportProgress = useCallback(() => {
    const scrollContainer = listScrollRef.current;
    const itemNodes = Array.from(
      scrollContainer?.querySelectorAll<HTMLElement>('[data-library-list-item="true"]') || []
    );
    const total = itemNodes.length;

    if (!scrollContainer || total === 0) {
      setViewportProgress(prev => (
        prev.reached === 0 && prev.total === 0 ? prev : { reached: 0, total: 0 }
      ));
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    let reached = total;

    if (containerRect.height > 0) {
      const viewportTop = containerRect.top;
      const viewportBottom = containerRect.bottom;
      let lastVisibleIndex = -1;

      itemNodes.forEach((node, index) => {
        const itemRect = node.getBoundingClientRect();
        if (itemRect.bottom > viewportTop && itemRect.top < viewportBottom) {
          lastVisibleIndex = index;
        }
      });

      if (lastVisibleIndex >= 0) {
        reached = lastVisibleIndex + 1;
      } else if (scrollContainer.scrollTop <= 0) {
        reached = 1;
      }
    }

    setViewportProgress(prev => (
      prev.reached === reached && prev.total === total ? prev : { reached, total }
    ));
  }, []);

  const normalizedLibrarySearchQuery = searchQuery.trim().toLocaleLowerCase();
  const isSearchExpanded = searchOpen || normalizedLibrarySearchQuery.length > 0;
  const activePlaylist = playlists.find(playlist => playlist.id === activePlaylistId) || null;
  const isPlaylistCollectionView = activeSurface === 'playlists' && !activePlaylistId;
  const selectionContext = useMemo<Pick<LibrarySelectionState, 'kind' | 'playlistId'>>(() => {
    if (activeSurface === 'playlists' && activePlaylistId) {
      return { kind: 'playlist-videos', playlistId: activePlaylistId };
    }
    if (activeSurface === 'playlists') {
      return { kind: 'playlists', playlistId: null };
    }
    return { kind: 'library-videos', playlistId: null };
  }, [activePlaylistId, activeSurface]);
  const isSelectionMode = Boolean(
    selection &&
    selection.kind === selectionContext.kind &&
    selection.playlistId === selectionContext.playlistId
  );
  const selectedIdSet = useMemo(() => new Set(
    isSelectionMode && selection ? selection.ids : []
  ), [isSelectionMode, selection]);
  const searchContentLabel = isPlaylistCollectionView ? 'playlist' : 'library';
  const currentPlaylistIdsByItem = useMemo(() => {
    const membershipsByItem = new Map<string, string[]>();
    Object.entries(playlistMemberships).forEach(([playlistId, memberships]) => {
      memberships.forEach(membership => {
        const key = playlistMembershipItemKey(membership);
        const currentIds = membershipsByItem.get(key) || [];
        membershipsByItem.set(key, [...currentIds, playlistId]);
      });
    });
    return membershipsByItem;
  }, [playlistMemberships]);
  const matchingPlaylists = useMemo(() => {
    if (!normalizedLibrarySearchQuery) return [];
    return playlists.filter(playlist => playlist.title
      .toLocaleLowerCase()
      .includes(normalizedLibrarySearchQuery));
  }, [normalizedLibrarySearchQuery, playlists]);
  const filteredPlaylists = useMemo(() => {
    if (!normalizedLibrarySearchQuery) return playlists;
    return matchingPlaylists;
  }, [matchingPlaylists, normalizedLibrarySearchQuery, playlists]);
  const videosByKey = useMemo(() => new Map(
    videos.map(video => [getLibraryProjectMembershipKey(video), video])
  ), [videos]);
  const playlistCoverItems = useMemo(() => {
    const covers: Record<string, NormalizedLibraryProject[]> = {};
    playlists.forEach(playlist => {
      covers[playlist.id] = [...(playlistMemberships[playlist.id] || [])]
        .sort(comparePlaylistMemberships)
        .map(membership => videosByKey.get(playlistMembershipItemKey(membership)))
        .filter((item): item is NormalizedLibraryProject => Boolean(item))
        .slice(0, PLAYLIST_COVER_GRID_LIMIT);
    });
    return covers;
  }, [playlistMemberships, playlists, videosByKey]);
  const orderedPlaylistVideos = useMemo(() => {
    if (!activePlaylistId) return videos;
    return [...(playlistMemberships[activePlaylistId] || [])]
      .sort(comparePlaylistMemberships)
      .map(membership => videosByKey.get(playlistMembershipItemKey(membership)))
      .filter((item): item is NormalizedLibraryProject => Boolean(item));
  }, [activePlaylistId, playlistMemberships, videos, videosByKey]);
  const activePlaylistMemberships = useMemo(() => {
    if (!activePlaylistId) return [];
    return [...(playlistMemberships[activePlaylistId] || [])].sort(comparePlaylistMemberships);
  }, [activePlaylistId, playlistMemberships]);
  const activePlaylistMembershipIndexByItem = useMemo(() => {
    const indexByItem = new Map<string, number>();
    activePlaylistMemberships.forEach((membership, index) => {
      indexByItem.set(`${membership.itemCollection}:${membership.itemId}`, index);
    });
    return indexByItem;
  }, [activePlaylistMemberships]);
  const scopedVideos = useMemo(() => {
    if (activePlaylistId) return orderedPlaylistVideos;
    // The flat Library surface only shows videos with no playlist membership —
    // items that belong to a playlist appear there (as a "folder"), not here,
    // so nothing is listed twice.
    if (activeSurface === 'library') {
      return videos.filter(video => (
        (currentPlaylistIdsByItem.get(getLibraryProjectMembershipKey(video)) || []).length === 0
      ));
    }
    return videos;
  }, [activePlaylistId, activeSurface, currentPlaylistIdsByItem, orderedPlaylistVideos, videos]);
  const selectedVideosForAction = useMemo(() => {
    if (!isSelectionMode || !selection || selection.kind === 'playlists') return [];
    const sourceVideos = selection.kind === 'playlist-videos' ? orderedPlaylistVideos : videos;
    return sourceVideos.filter(video => selectedIdSet.has(getLibraryProjectMembershipKey(video)));
  }, [isSelectionMode, orderedPlaylistVideos, selectedIdSet, selection, videos]);
  const selectedPlaylistsForAction = useMemo(() => {
    if (!isSelectionMode || !selection || selection.kind !== 'playlists') return [];
    return playlists.filter(playlist => selectedIdSet.has(playlist.id));
  }, [isSelectionMode, playlists, selectedIdSet, selection]);
  const selectedPlaylistMembershipsForAction = useMemo(() => {
    if (!isSelectionMode || !selection || selection.kind !== 'playlist-videos') return [];
    return activePlaylistMemberships.filter(membership => (
      selectedIdSet.has(`${membership.itemCollection}:${membership.itemId}`)
    ));
  }, [activePlaylistMemberships, isSelectionMode, selectedIdSet, selection]);
  const selectedActionCount = selection?.kind === 'playlists'
    ? selectedPlaylistsForAction.length
    : selectedVideosForAction.length;
  const selectedActionNoun = selection?.kind === 'playlists' ? 'playlist' : 'video';
  const selectedPlaylistForRename = selection?.kind === 'playlists' && selectedPlaylistsForAction.length === 1
    ? selectedPlaylistsForAction[0]
    : null;
  const filteredVideos = useMemo(() => {
    return scopedVideos.filter(video =>
      matchesLibraryTitleSearch(
        video,
        normalizedLibrarySearchQuery
      ) &&
      matchesLibrarySourceFilter(video, sourceFilter) &&
      matchesLibraryStatusFilter(video, statusFilter)
    );
  }, [scopedVideos, normalizedLibrarySearchQuery, sourceFilter, statusFilter]);
  const hasActiveLibraryFilters = (
    (!isPlaylistCollectionView && normalizedLibrarySearchQuery.length > 0) ||
    (!isPlaylistCollectionView && sourceFilter !== 'all') ||
    (!isPlaylistCollectionView && statusFilter !== 'all')
  );
  const activeFilterCount = Number(sourceFilter !== 'all') + Number(statusFilter !== 'all');
  const headerTitle = activePlaylist?.title || (activeSurface === 'playlists' ? 'Playlists' : 'Library');
  const progressNoun = isPlaylistCollectionView ? 'playlist' : 'item';
  const progressTotal = isPlaylistCollectionView ? filteredPlaylists.length : filteredVideos.length;
  const progressReached = progressTotal === 0
    ? 0
    : Math.min(
      progressTotal,
      Math.max(1, viewportProgress.total === progressTotal ? viewportProgress.reached : progressTotal)
    );
  const progressLabel = `${progressReached} / ${progressTotal}`;
  const progressAriaLabel = formatProgressLaneAriaLabel(progressReached, progressTotal, progressNoun);
  const progressRatio = progressTotal === 0 ? 0 : progressReached / progressTotal;
  const canReorderPlaylist = Boolean(
    activePlaylistId &&
    !isSelectionMode &&
    activePlaylistMemberships.length > 1 &&
    orderedPlaylistVideos.length > 1 &&
    !normalizedLibrarySearchQuery &&
    sourceFilter === 'all' &&
    statusFilter === 'all'
  );

  // Load thumbnail from Firebase Storage
  const loadThumbnail = useCallback(async (video: NormalizedLibraryProject) => {
    const thumbnailKey = getLibraryProjectMembershipKey(video);
    const youtubeThumbnailUrl = getLibraryProjectYouTubeThumbnailUrl(video);
    if (youtubeThumbnailUrl) {
      setThumbnailUrls(prev => ({ ...prev, [thumbnailKey]: youtubeThumbnailUrl }));
      return;
    }

    const thumbnailPath = getLibraryProjectThumbnailPath(video);
    if (!thumbnailPath) {
      // No thumbnail path, use placeholder
      setThumbnailUrls(prev => ({
        ...prev,
        [thumbnailKey]: generatePlaceholderThumbnail(video.title)
      }));
      return;
    }

    setThumbnailsLoading(prev => ({ ...prev, [thumbnailKey]: true }));
    
    try {
      const url = await VideoThumbnailService.getThumbnailUrl(thumbnailPath);
      const version = toCacheVersion(video.updatedAt) || toCacheVersion(video.createdAt);
      const cacheBustedUrl = version
        ? `${url}${url.includes('?') ? '&' : '?'}v=${version}`
        : url;
      setThumbnailUrls(prev => ({ ...prev, [thumbnailKey]: cacheBustedUrl }));
    } catch (error) {
      console.warn(`Failed to load thumbnail for ${video.title}:`, error);
      // Fallback to placeholder
      setThumbnailUrls(prev => ({
        ...prev,
        [thumbnailKey]: generatePlaceholderThumbnail(video.title)
      }));
    } finally {
      setThumbnailsLoading(prev => ({ ...prev, [thumbnailKey]: false }));
    }
  }, []);

  // Check if user has any other active shares
  const checkForOtherActiveShares = async (currentVideoId: string): Promise<SharedSession | null> => {
    if (!userProfile) return null;
    
    try {
      const sharesRef = collection(db, 'sharedSessions');
      const q = query(
        sharesRef,
        where('ownerId', '==', userProfile.uid),
        where('isActive', '==', true)
      );
      const querySnapshot = await getDocs(q);
      
      for (const doc of querySnapshot.docs) {
        const data = doc.data();
        if (data.videoId !== currentVideoId) {
          return {
            shareId: doc.id,
            ...data
          } as SharedSession;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error checking for active shares:', error);
      return null;
    }
  };

  // Load share data for all videos
  const loadShareData = useCallback(async (videos: NormalizedLibraryProject[]) => {
    if (!sharingEnabled) {
      setShareDataMap({});
      return;
    }
    if (!userProfile) return;
    
    console.log('🔍 Loading share data for', videos.length, 'videos');
    
    const shareMap: Record<string, SharedSession | null> = {};
    
    for (const video of videos) {
      if (isYouTubeLibraryProject(video) || isLocalLibraryProject(video)) {
        shareMap[video.id] = null;
        continue;
      }

      try {
        console.log(`  Checking video: ${video.id} (${video.title})`);
        const share = await getActiveShareForVideo(userProfile.uid, video.id);
        shareMap[video.id] = share;
        
        if (share) {
          console.log(`  ✅ Found active share for ${video.title}`);
        } else {
          console.log(`  ℹ️ No active share for ${video.title}`);
        }
      } catch (error) {
        console.error(`  ❌ Error loading share for video ${video.id}:`, error);
        shareMap[video.id] = null;
      }
    }
    
    console.log('📊 Share data loaded:', Object.keys(shareMap).length, 'videos processed');
    console.log('📊 Active shares found:', Object.values(shareMap).filter(s => s !== null).length);
    
    setShareDataMap(shareMap);
  }, [sharingEnabled, userProfile]);

  const loadVideos = useCallback(async () => {
    setLoading(true);
    setError(null);
    let playlistLoadError: unknown = null;
    try {
      const [videoList, playlistList] = await Promise.all([
        listLibraryProjects(),
        listPlaylists().catch(err => {
          console.warn('Failed to read playlists collection:', err);
          playlistLoadError = err;
          return [] as LibraryPlaylist[];
        })
      ]);

      // Render the library items as soon as their own collections are loaded.
      // Playlist membership is optional metadata: a rules deployment or an
      // older account must never make the entire library look empty.
      setVideos(videoList);
      setPlaylists(playlistList);
      setPlaylistsLoaded(true);

      if (playlistLoadError) {
        setError(getPlaylistMembershipErrorMessage(playlistLoadError));
      }

      const legacyMembershipMap: Record<string, LibraryPlaylistMembership[]> = {};
      playlistList.forEach(playlist => {
        legacyMembershipMap[playlist.id] = [];
      });
      videoList.forEach(video => {
        if (!video.playlistId || !legacyMembershipMap[video.playlistId]) return;
        legacyMembershipMap[video.playlistId].push(createLibraryProjectPlaylistMembership(
          video.playlistId,
          video,
          legacyMembershipMap[video.playlistId].length,
          { legacy: true }
        ));
      });
      setPlaylistMemberships(legacyMembershipMap);

      try {
        if (playlistList.length > 0) {
          const membershipMap = await listPlaylistMembershipsForPlaylists(
            playlistList.map(playlist => playlist.id)
          );
          setPlaylistMemberships(membershipMap);
        }
      } catch (membershipError) {
        console.warn(
          'Could not load playlist memberships; showing library items with legacy playlist metadata.',
          membershipError
        );
        setError(getPlaylistMembershipErrorMessage(membershipError));
      }
      
      // Load thumbnails for each video
      videoList.forEach(video => {
        loadThumbnail(video);
      });
      
      // Load share data for all videos
      if (sharingEnabled) {
        await loadShareData(videoList);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load videos');
    } finally {
      setLoading(false);
    }
  }, [loadShareData, loadThumbnail, sharingEnabled]);

  useEffect(() => {
    if (isOpen) {
      loadVideos();
    }
  }, [isOpen, loadVideos]);

  useEffect(() => {
    if (userProfile) return;
    setActiveSurface('library');
    setActivePlaylistId(null);
  }, [userProfile]);

  useEffect(() => {
    writeStoredLibraryLocation(userProfile?.uid, { surface: activeSurface, playlistId: activePlaylistId });
  }, [activePlaylistId, activeSurface, userProfile?.uid]);

  useEffect(() => {
    // Wait for the first playlist load so a restored playlist isn't dropped before it exists.
    if (!activePlaylistId || !playlistsLoaded) return;
    if (playlists.some(playlist => playlist.id === activePlaylistId)) return;
    setActivePlaylistId(null);
    setSelection(null);
  }, [activePlaylistId, playlists, playlistsLoaded]);

  useEffect(() => {
    setSelection(prev => {
      if (!prev) return prev;

      const availableIds = new Set<string>();
      if (prev.kind === 'playlists') {
        playlists.forEach(playlist => availableIds.add(playlist.id));
      } else if (prev.kind === 'playlist-videos' && prev.playlistId) {
        (playlistMemberships[prev.playlistId] || []).forEach(membership => {
          const key = playlistMembershipItemKey(membership);
          if (videosByKey.has(key)) availableIds.add(key);
        });
      } else {
        videos.forEach(video => availableIds.add(getLibraryProjectMembershipKey(video)));
      }

      const ids = prev.ids.filter(id => availableIds.has(id));
      if (ids.length === prev.ids.length) return prev;
      return ids.length > 0 ? { ...prev, ids } : null;
    });
  }, [playlistMemberships, playlists, videos, videosByKey]);

  useEffect(() => {
    if (!searchOpen) return;
    searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    updateViewportProgress();
  }, [
    activePlaylistId,
    activeSurface,
    filteredPlaylists.length,
    filteredVideos.length,
    loading,
    updateViewportProgress
  ]);

  useEffect(() => {
    if (!actionToast) return;
    const timeoutId = window.setTimeout(() => setActionToast(null), 4500);
    return () => window.clearTimeout(timeoutId);
  }, [actionToast]);

  useEffect(() => {
    return () => clearLongPressSelectionTimer();
  }, [clearLongPressSelectionTimer]);

  useEffect(() => {
    if (!searchOpen) return;
    const handleOutsideSearchClick = (event: MouseEvent) => {
      const target = event.target as Node;
      // Clicking a search result (a video/playlist card) must never be
      // treated as "dismissing" search — only closing/clearing when the
      // click lands truly outside both the search box and the results list
      // it's filtering, so selecting a found item actually loads it instead
      // of the search state resetting out from under the click.
      if (searchContainerRef.current?.contains(target)) return;
      if (listScrollRef.current?.contains(target)) return;
      setSearchQuery('');
      setSearchOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideSearchClick);
    return () => document.removeEventListener('mousedown', handleOutsideSearchClick);
  }, [searchOpen, searchQuery]);

  const handleSelectSurface = (surface: LibrarySurface) => {
    clearLongPressSelectionTimer();
    setActiveSurface(surface);
    setActivePlaylistId(null);
    setSelection(null);
    setOpenVideoMenuId(null);
    setOpenPlaylistPickerId(null);
    setEditingPlaylistId(null);
    setNewPlaylistTitle('');
    setSearchOpen(false);
    setSearchQuery('');
    setFiltersOpen(false);
    setSourceFilter('all');
    setStatusFilter('all');
  };

  const handleCloseSearch = () => {
    setSearchQuery('');
    setSearchOpen(false);
    searchInputRef.current?.blur();
  };

  const handleSelectPlaylist = (playlistId: string) => {
    clearLongPressSelectionTimer();
    setActiveSurface('playlists');
    setActivePlaylistId(playlistId);
    setSelection(null);
    setEditingPlaylistId(null);
    setNewPlaylistTitle('');
    setSearchQuery('');
    setSearchOpen(false);
  };

  const handleCancelSelection = () => {
    clearLongPressSelectionTimer();
    setSelection(null);
    setOpenVideoMenuId(null);
    setOpenPlaylistPickerId(null);
    setEditingPlaylistId(null);
    setNewPlaylistTitle('');
  };

  const handleToggleSelection = (kind: LibrarySelectionKind, id: string) => {
    const playlistId = kind === 'playlist-videos' ? activePlaylistId : null;
    if (kind === 'playlist-videos' && !playlistId) return;

    setEditingTitle(null);
    setEditingPlaylistId(null);
    setNewPlaylistTitle('');
    setOpenVideoMenuId(null);
    setOpenPlaylistPickerId(null);
    setSelection(prev => {
      const sameContext = Boolean(
        prev &&
        prev.kind === kind &&
        prev.playlistId === playlistId
      );
      if (!sameContext || !prev) {
        return { kind, playlistId, ids: [id] };
      }

      const isSelected = prev.ids.includes(id);
      const ids = isSelected
        ? prev.ids.filter(selectedId => selectedId !== id)
        : [...prev.ids, id];
      return ids.length > 0 ? { ...prev, ids } : null;
    });
  };

  const handleSelectionLongPressStart = (
    event: React.PointerEvent<HTMLElement>,
    kind: LibrarySelectionKind,
    id: string
  ) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (bulkActionInProgress || shouldIgnoreSelectionGesture(event.target)) return;

    clearLongPressSelectionTimer();
    longPressSelectionRef.current = {
      kind,
      id,
      startX: event.clientX,
      startY: event.clientY
    };
    longPressTimerRef.current = window.setTimeout(() => {
      const pendingSelection = longPressSelectionRef.current;
      if (!pendingSelection) return;

      suppressNextCardClickRef.current = true;
      handleToggleSelection(pendingSelection.kind, pendingSelection.id);
      clearLongPressSelectionTimer();
    }, LONG_PRESS_SELECTION_DELAY_MS);
  };

  const handleSelectionPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const pendingSelection = longPressSelectionRef.current;
    if (!pendingSelection) return;

    const movedX = Math.abs(event.clientX - pendingSelection.startX);
    const movedY = Math.abs(event.clientY - pendingSelection.startY);
    if (
      movedX > LONG_PRESS_SELECTION_MOVE_TOLERANCE_PX ||
      movedY > LONG_PRESS_SELECTION_MOVE_TOLERANCE_PX
    ) {
      clearLongPressSelectionTimer();
    }
  };

  const shouldSuppressCardClickAfterLongPress = (event: React.MouseEvent<HTMLElement>) => {
    if (!suppressNextCardClickRef.current) return false;
    event.preventDefault();
    event.stopPropagation();
    suppressNextCardClickRef.current = false;
    return true;
  };

  const handleSelectionCardKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    kind: LibrarySelectionKind,
    id: string,
    primaryAction: () => void
  ) => {
    // Keys typed into a field inside the card (e.g. renaming) belong to that field, so Space still
    // types a space instead of toggling selection.
    if (event.target !== event.currentTarget) return;
    if (event.key === ' ') {
      event.preventDefault();
      handleToggleSelection(kind, id);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (isSelectionMode) {
        handleToggleSelection(kind, id);
      } else {
        primaryAction();
      }
    }
  };

  const getPlaylistMembershipIndex = (video: NormalizedLibraryProject): number => (
    activePlaylistMembershipIndexByItem.get(getLibraryProjectMembershipKey(video)) ?? -1
  );

  const handleReorderPlaylist = async (fromIndex: number, toIndex: number) => {
    if (!activePlaylistId || reorderingPlaylist || fromIndex < 0 || toIndex < 0) return;
    if (fromIndex === toIndex || fromIndex >= activePlaylistMemberships.length || toIndex >= activePlaylistMemberships.length) return;

    const previousMemberships = activePlaylistMemberships;
    const nextMemberships = [...previousMemberships];
    const [movedMembership] = nextMemberships.splice(fromIndex, 1);
    nextMemberships.splice(toIndex, 0, movedMembership);

    setPlaylistMemberships(prev => ({
      ...prev,
      [activePlaylistId]: nextMemberships
    }));
    setReorderingPlaylist(true);
    setError(null);

    try {
      const persistedMemberships = await reorderPlaylistMemberships(activePlaylistId, nextMemberships);
      setPlaylistMemberships(prev => ({
        ...prev,
        [activePlaylistId]: persistedMemberships
      }));
    } catch (err) {
      setPlaylistMemberships(prev => ({
        ...prev,
        [activePlaylistId]: previousMemberships
      }));
      const message = getPlaylistErrorMessage(err, 'Failed to reorder playlist');
      setError(`${message}. Your previous playlist order was restored. Try again.`);
    } finally {
      setReorderingPlaylist(false);
      setDraggedPlaylistItemKey(null);
      setDragOverPlaylistItemKey(null);
    }
  };

  const handleMovePlaylistItem = (video: NormalizedLibraryProject, direction: -1 | 1) => {
    if (!canReorderPlaylist) return;
    const currentIndex = getPlaylistMembershipIndex(video);
    if (currentIndex < 0) return;
    void handleReorderPlaylist(currentIndex, currentIndex + direction);
  };

  const handlePlaylistDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    video: NormalizedLibraryProject
  ) => {
    clearLongPressSelectionTimer();
    if (!canReorderPlaylist || reorderingPlaylist) return;
    const itemKey = getLibraryProjectMembershipKey(video);
    setDraggedPlaylistItemKey(itemKey);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', itemKey);
    }
    event.stopPropagation();
  };

  const handlePlaylistDragOver = (
    event: React.DragEvent<HTMLDivElement>,
    video: NormalizedLibraryProject
  ) => {
    if (!draggedPlaylistItemKey || draggedPlaylistItemKey === getLibraryProjectMembershipKey(video)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    setDragOverPlaylistItemKey(getLibraryProjectMembershipKey(video));
  };

  const handlePlaylistDrop = (
    event: React.DragEvent<HTMLDivElement>,
    video: NormalizedLibraryProject
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (!draggedPlaylistItemKey) return;
    const fromIndex = activePlaylistMembershipIndexByItem.get(draggedPlaylistItemKey) ?? -1;
    const toIndex = getPlaylistMembershipIndex(video);
    void handleReorderPlaylist(fromIndex, toIndex);
  };

  const handlePlaylistDragEnd = () => {
    setDraggedPlaylistItemKey(null);
    setDragOverPlaylistItemKey(null);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSearchOpen(false);
    setSourceFilter('all');
    setStatusFilter('all');
  };

  const handleLoadVideo = async (video: NormalizedLibraryProject) => {
    try {
      setLoading(true);
      setError(null);

      const videoUrl = await getLibraryProjectVideoUrl(video);
      
      onLoadVideo(video, videoUrl);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const deleteLibraryItem = async (item: NormalizedLibraryProject) => {
    if (!isLocalLibraryProject(item)) {
      await removeLibraryProjectFromAllPlaylists(item);
    }
    await deleteLibraryProject(item);
  };

  const removeDeletedVideoFromState = (item: NormalizedLibraryProject) => {
    const itemKey = getLibraryProjectMembershipKey(item);
    setVideos(prev => prev.filter(v => getLibraryProjectMembershipKey(v) !== itemKey));
    setPlaylistMemberships(prev => {
      const next: Record<string, LibraryPlaylistMembership[]> = {};
      Object.entries(prev).forEach(([playlistId, memberships]) => {
        next[playlistId] = memberships.filter(membership => (
          !playlistMembershipMatchesLibraryProject(membership, item)
        ));
      });
      return next;
    });
    setOpenVideoMenuId(prev => prev === item.id ? null : prev);
    setOpenPlaylistPickerId(prev => prev === item.id ? null : prev);
  };

  const refreshLibraryCountAfterDeletes = async (items: NormalizedLibraryProject[]) => {
    if (!userProfile) return;
    const uploadSlotDeletes = items.filter(libraryProjectConsumesUploadSlot).length;
    if (uploadSlotDeletes === 0) return;

    for (let index = 0; index < uploadSlotDeletes; index += 1) {
      await decrementLibraryCount(userProfile.uid);
    }
    await refreshProfile();
  };

  const handleDeleteVideo = async (videoId: string) => {
    const item = videos.find(v => v.id === videoId);
    if (!item) return;

    if (!window.confirm('Are you sure you want to delete this video? This cannot be undone.')) {
      return;
    }

    setDeletingId(videoId);
    setError(null);

    try {
      await deleteLibraryItem(item);
      removeDeletedVideoFromState(item);
      await refreshLibraryCountAfterDeletes([item]);
      console.log('🟢 Video deleted successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete video');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditTitle = (video: NormalizedLibraryProject) => {
    setEditingTitle(video.id);
    setNewTitle(video.title);
  };

  const handleSaveTitle = async (videoId: string) => {
    if (!newTitle.trim()) return;

    try {
      const item = videos.find(v => v.id === videoId);
      if (!item) return;
      await renameLibraryProject(item, newTitle.trim());

      setVideos(prev => prev.map(v =>
        v.id === videoId ? { ...v, title: newTitle.trim() } : v
      ));

      setEditingTitle(null);
      console.log('🟢 Title updated successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update title');
    }
  };

  const editingVideoForRename = editingTitle
    ? videos.find(video => video.id === editingTitle) ?? null
    : null;
  const editingPlaylistForRename = editingPlaylistId
    ? playlists.find(playlist => playlist.id === editingPlaylistId) ?? null
    : null;

  const handleCancelEdit = () => {
    setEditingTitle(null);
    setNewTitle('');
  };

  const handleStartPlaylistRename = (playlist: LibraryPlaylist) => {
    setEditingTitle(null);
    setSelection(null);
    setError(null);
    setEditingPlaylistId(playlist.id);
    setNewPlaylistTitle(playlist.title);
  };

  const handleCancelPlaylistRename = () => {
    setEditingPlaylistId(null);
    setNewPlaylistTitle('');
  };

  const handleSavePlaylistTitle = async (playlistId: string) => {
    if (!userProfile || renamingPlaylistId) return;

    const playlist = playlists.find(current => current.id === playlistId);
    if (!playlist) return;

    const cleanTitle = normalizePlaylistTitle(newPlaylistTitle);
    if (!cleanTitle) return;

    if (cleanTitle === playlist.title) {
      handleCancelPlaylistRename();
      return;
    }

    const existingPlaylist = playlists.find(current => (
      current.id !== playlistId &&
      current.title.toLocaleLowerCase() === cleanTitle.toLocaleLowerCase()
    ));
    if (existingPlaylist) {
      setError('A playlist with this name already exists');
      return;
    }

    setRenamingPlaylistId(playlistId);
    setError(null);

    try {
      const updatedPlaylist = await renamePlaylist(playlistId, cleanTitle);
      setPlaylists(prev => prev
        .map(current => current.id === playlistId ? { ...current, ...updatedPlaylist } : current)
        .sort((a, b) => a.title.localeCompare(b.title)));
      setEditingPlaylistId(null);
      setNewPlaylistTitle('');
      setActionToast({
        message: 'Playlist renamed',
        undo: async () => {
          const restoredPlaylist = await renamePlaylist(playlistId, playlist.title);
          setPlaylists(prev => prev
            .map(current => current.id === playlistId ? { ...current, ...restoredPlaylist } : current)
            .sort((a, b) => a.title.localeCompare(b.title)));
        }
      });
    } catch (err) {
      setError(getPlaylistErrorMessage(err, 'Failed to rename playlist'));
    } finally {
      setRenamingPlaylistId(null);
    }
  };

  const handleCreatePlaylist = async () => {
    if (!userProfile || creatingPlaylist) return;

    const requestedTitle = window.prompt('Playlist title');
    if (requestedTitle === null) return;

    const cleanTitle = normalizePlaylistTitle(requestedTitle);
    if (!cleanTitle) return;

    const existingPlaylist = playlists.find(
      playlist => playlist.title.toLocaleLowerCase() === cleanTitle.toLocaleLowerCase()
    );

    if (existingPlaylist) {
      setError('A playlist with this name already exists');
      return;
    }

    setCreatingPlaylist(true);
    setError(null);

    try {
      const playlist = await createPlaylist(cleanTitle);
      setPlaylists(prev => [...prev, playlist].sort((a, b) => a.title.localeCompare(b.title)));
      handleSelectPlaylist(playlist.id);
    } catch (err) {
      setError(getPlaylistErrorMessage(err, 'Failed to create playlist'));
    } finally {
      setCreatingPlaylist(false);
    }
  };

  const handleSetPlaylist = async (video: NormalizedLibraryProject, selectedPlaylistId: string) => {
    if (!userProfile || updatingPlaylistId === video.id) return;

    const currentPlaylistIds = currentPlaylistIdsByItem.get(getLibraryProjectMembershipKey(video)) || [];
    const isMember = currentPlaylistIds.includes(selectedPlaylistId);
    const playlistTitle = playlists.find(playlist => playlist.id === selectedPlaylistId)?.title || 'playlist';

    setUpdatingPlaylistId(video.id);
    setError(null);

    try {
      if (isMember) {
        await removeLibraryProjectFromPlaylist(selectedPlaylistId, video);
      } else {
        await addLibraryProjectToPlaylist(selectedPlaylistId, video);
      }

      setPlaylistMemberships(prev => {
        const memberships = prev[selectedPlaylistId] || [];
        if (isMember) {
          return {
            ...prev,
            [selectedPlaylistId]: memberships.filter(membership => !(
              playlistMembershipMatchesLibraryProject(membership, video)
            ))
          };
        }
        return {
          ...prev,
          [selectedPlaylistId]: [
            ...memberships,
            createLibraryProjectPlaylistMembership(selectedPlaylistId, video, memberships.length)
          ]
        };
      });

      setOpenPlaylistPickerId(null);
      setActionToast({
        message: isMember ? `Removed from ${playlistTitle}` : `Added to ${playlistTitle}`,
        undo: async () => {
          if (isMember) {
            const membership = await addLibraryProjectToPlaylist(selectedPlaylistId, video);
            setPlaylistMemberships(prev => ({
              ...prev,
              [selectedPlaylistId]: [...(prev[selectedPlaylistId] || []), membership]
            }));
          } else {
            await removeLibraryProjectFromPlaylist(selectedPlaylistId, video);
            setPlaylistMemberships(prev => ({
              ...prev,
              [selectedPlaylistId]: (prev[selectedPlaylistId] || []).filter(membership => !(
                playlistMembershipMatchesLibraryProject(membership, video)
              ))
            }));
          }
        }
      });
    } catch (err) {
      setError(getPlaylistErrorMessage(err, 'Failed to update playlist'));
    } finally {
      setUpdatingPlaylistId(null);
    }
  };

  const handleRemoveFromPlaylist = async (video: NormalizedLibraryProject, playlistId: string) => {
    if (!userProfile || updatingPlaylistId === video.id) return;

    const currentPlaylistIds = currentPlaylistIdsByItem.get(getLibraryProjectMembershipKey(video)) || [];
    if (!currentPlaylistIds.includes(playlistId)) return;

    const playlistTitle = playlists.find(playlist => playlist.id === playlistId)?.title || 'playlist';
    setUpdatingPlaylistId(video.id);
    setError(null);

    try {
      await removeLibraryProjectFromPlaylist(playlistId, video);
      setPlaylistMemberships(prev => ({
        ...prev,
        [playlistId]: (prev[playlistId] || []).filter(membership => !(
          playlistMembershipMatchesLibraryProject(membership, video)
        ))
      }));
      setOpenVideoMenuId(null);
      setOpenPlaylistPickerId(null);
      setActionToast({
        message: `Removed from ${playlistTitle}`,
        undo: async () => {
          const membership = await addLibraryProjectToPlaylist(playlistId, video);
          setPlaylistMemberships(prev => ({
            ...prev,
            [playlistId]: [...(prev[playlistId] || []), membership]
          }));
        }
      });
    } catch (err) {
      setError(getPlaylistErrorMessage(err, 'Failed to remove item from playlist'));
    } finally {
      setUpdatingPlaylistId(null);
    }
  };

  const handleBulkDeleteVideos = async () => {
    const items = selectedVideosForAction;
    if (items.length === 0 || bulkActionInProgress) return;

    const label = items.length === 1 ? 'video' : 'videos';
    if (!window.confirm(`Delete ${items.length} selected ${label}? This deletes the library item and cannot be undone.`)) {
      return;
    }

    setBulkActionInProgress(true);
    setError(null);
    setOpenVideoMenuId(null);
    setOpenPlaylistPickerId(null);

    const succeeded: NormalizedLibraryProject[] = [];
    const failed: Array<{ item: NormalizedLibraryProject; error: unknown }> = [];

    for (const item of items) {
      setDeletingId(item.id);
      try {
        await deleteLibraryItem(item);
        succeeded.push(item);
        removeDeletedVideoFromState(item);
      } catch (err) {
        failed.push({ item, error: err });
      }
    }

    try {
      await refreshLibraryCountAfterDeletes(succeeded);
    } catch (err) {
      setError(err instanceof Error
        ? `Deleted ${succeeded.length} ${label}, but failed to refresh storage count: ${err.message}`
        : `Deleted ${succeeded.length} ${label}, but failed to refresh storage count`);
    }

    if (failed.length > 0) {
      setSelection(prev => prev ? {
        ...prev,
        ids: failed.map(({ item }) => getLibraryProjectMembershipKey(item))
      } : prev);
      setError(describeBulkFailure(
        'Deleted',
        succeeded.length,
        items.length,
        failed.map(({ item, error }) => ({ title: item.title, error }))
      ));
    } else {
      handleCancelSelection();
    }

    setDeletingId(null);
    setBulkActionInProgress(false);
  };

  const handleBulkRemoveFromPlaylist = async () => {
    if (!activePlaylistId || !activePlaylist || bulkActionInProgress) return;

    const items = selectedVideosForAction;
    if (items.length === 0) return;

    const membershipsByItemKey = new Map(
      selectedPlaylistMembershipsForAction.map(membership => [
        playlistMembershipItemKey(membership),
        membership
      ])
    );
    const succeeded: Array<{ item: NormalizedLibraryProject; membership: LibraryPlaylistMembership }> = [];
    const failed: Array<{ item: NormalizedLibraryProject; error: unknown }> = [];

    setBulkActionInProgress(true);
    setError(null);
    setOpenVideoMenuId(null);
    setOpenPlaylistPickerId(null);

    for (const item of items) {
      const itemKey = getLibraryProjectMembershipKey(item);
      const membership = membershipsByItemKey.get(itemKey) ||
        createLibraryProjectPlaylistMembership(activePlaylistId, item, activePlaylistMemberships.length);

      setUpdatingPlaylistId(item.id);
      try {
        await removeLibraryProjectFromPlaylist(activePlaylistId, item);
        succeeded.push({ item, membership });
        setPlaylistMemberships(prev => ({
          ...prev,
          [activePlaylistId]: (prev[activePlaylistId] || []).filter(current => !(
            playlistMembershipMatchesLibraryProject(current, item)
          ))
        }));
      } catch (err) {
        failed.push({ item, error: err });
      }
    }

    if (failed.length > 0) {
      setSelection(prev => prev ? {
        ...prev,
        ids: failed.map(({ item }) => getLibraryProjectMembershipKey(item))
      } : prev);
      setError(describeBulkFailure(
        'Removed',
        succeeded.length,
        items.length,
        failed.map(({ item, error }) => ({ title: item.title, error }))
      ));
    } else {
      handleCancelSelection();
      const removedCount = succeeded.length;
      const playlistId = activePlaylistId;
      const playlistTitle = activePlaylist.title;
      setActionToast({
        message: `Removed ${removedCount} ${removedCount === 1 ? 'video' : 'videos'} from ${playlistTitle}`,
        undo: async () => {
          const restoredMemberships = await Promise.all(succeeded.map(({ item, membership }) => (
            addLibraryProjectToPlaylist(playlistId, item, membership.position)
          )));
          setPlaylistMemberships(prev => {
            const restoredKeys = new Set(
              restoredMemberships.map(playlistMembershipItemKey)
            );
            const currentMemberships = (prev[playlistId] || []).filter(membership => !(
              restoredKeys.has(playlistMembershipItemKey(membership))
            ));
            return {
              ...prev,
              [playlistId]: [...currentMemberships, ...restoredMemberships].sort(comparePlaylistMemberships)
            };
          });
        }
      });
    }

    setUpdatingPlaylistId(null);
    setBulkActionInProgress(false);
  };

  const handleBulkDeletePlaylists = async () => {
    const selectedPlaylists = selectedPlaylistsForAction;
    if (selectedPlaylists.length === 0 || bulkActionInProgress) return;

    const label = selectedPlaylists.length === 1 ? 'playlist' : 'playlists';
    if (!window.confirm(`Delete ${selectedPlaylists.length} selected ${label}? Videos inside them will stay in your Library.`)) {
      return;
    }

    setBulkActionInProgress(true);
    setError(null);
    setOpenVideoMenuId(null);
    setOpenPlaylistPickerId(null);

    const succeeded: LibraryPlaylist[] = [];
    const failed: Array<{ playlist: LibraryPlaylist; error: unknown }> = [];

    for (const playlist of selectedPlaylists) {
      try {
        await deletePlaylist(playlist.id);
        succeeded.push(playlist);
        setPlaylists(prev => prev.filter(current => current.id !== playlist.id));
        setPlaylistMemberships(prev => {
          const next = { ...prev };
          delete next[playlist.id];
          return next;
        });
      } catch (err) {
        failed.push({ playlist, error: err });
      }
    }

    if (failed.length > 0) {
      setSelection(prev => prev ? {
        ...prev,
        ids: failed.map(({ playlist }) => playlist.id)
      } : prev);
      setError(describeBulkFailure(
        'Deleted',
        succeeded.length,
        selectedPlaylists.length,
        failed.map(({ playlist, error }) => ({ title: playlist.title, error }))
      ));
    } else {
      handleCancelSelection();
    }

    setBulkActionInProgress(false);
  };

  const handleCreatePlaylistForVideo = async (video: NormalizedLibraryProject, title: string) => {
    if (!userProfile || updatingPlaylistId === video.id || creatingPlaylist) return;

    const cleanTitle = normalizePlaylistTitle(title);
    if (!cleanTitle) return;

    const existingPlaylist = playlists.find(
      playlist => playlist.title.toLocaleLowerCase() === cleanTitle.toLocaleLowerCase()
    );

    if (existingPlaylist) {
      setError('A playlist with this name already exists');
      return;
    }

    try {
      setError(null);
      setCreatingPlaylist(true);
      const playlist = await createPlaylist(cleanTitle);
      setPlaylists(prev => [...prev, playlist].sort((a, b) => a.title.localeCompare(b.title)));
      await handleSetPlaylist(video, playlist.id);
    } catch (err) {
      setError(getPlaylistErrorMessage(err, 'Failed to create playlist'));
    } finally {
      setCreatingPlaylist(false);
    }
  };

  const handleCycleLearningStatus = async (
    event: React.MouseEvent<HTMLButtonElement>,
    video: NormalizedLibraryProject
  ) => {
    event.stopPropagation();
    if (updatingStatusId === video.id) return;

    const nextStatus = getNextLearningStatus(video.learningStatus);

    setUpdatingStatusId(video.id);
    setError(null);

    try {
      await updateLibraryProjectLearningStatus(video, nextStatus);
      setVideos(prev => prev.map(item => {
        if (item.id !== video.id) return item;
        const updatedItem: NormalizedLibraryProject = { ...item };
        if (nextStatus) {
          updatedItem.learningStatus = nextStatus;
        } else {
          delete updatedItem.learningStatus;
        }
        return updatedItem;
      }));
      setActionToast({
        message: `Status: ${getLearningStatusLabel(nextStatus)}`,
        undo: async () => {
          await updateLibraryProjectLearningStatus(video, video.learningStatus);
          setVideos(prev => prev.map(item => {
            if (item.id !== video.id) return item;
            const restoredItem: NormalizedLibraryProject = { ...item };
            if (video.learningStatus) restoredItem.learningStatus = video.learningStatus;
            else delete restoredItem.learningStatus;
            return restoredItem;
          }));
        }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update learning status');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  // Handle share creation with warning
  const handleCreateShare = async (video: NormalizedLibraryProject) => {
    if (!sharingEnabled) return;
    if (!userProfile) return;
    const storagePath = getLibraryProjectStoragePath(video);
    if (!storagePath) {
      setError('Sharing is available for cloud-uploaded videos only.');
      return;
    }
    
    const existingShare = await checkForOtherActiveShares(video.id);
    
    if (existingShare) {
      const existingVideo = videos.find(v => v.id === existingShare.videoId);
      const existingVideoTitle = existingVideo?.title || 'another video';
      
      const confirmMessage = 
        `⚠️ SHARE LIMIT\n\n` +
        `You can only have 1 active share at a time.\n\n` +
        `Your current share for "${existingVideoTitle}" will be deactivated.\n\n` +
        `Do you want to continue?`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }
    
    setLoadingShares(prev => ({ ...prev, [video.id]: true }));
    
    try {
      const result = await createSharedSession(
        video.id,
        storagePath,
        video.title,
        video.segments,
        video.settings,
        userProfile.uid
      );

      await markLibraryProjectShared(video, result);
      
      await loadShareData(videos);
      
      console.log('🟢 Share created successfully');
    } catch (error) {
      console.error('Failed to create share:', error);
      setError(error instanceof Error ? error.message : 'Failed to create share');
    } finally {
      setLoadingShares(prev => ({ ...prev, [video.id]: false }));
    }
  };

  const librarySurfaceSwitcher = (
    <div
      role="tablist"
      aria-label="Library views"
      style={{
        display: 'flex',
        flex: '0 0 auto',
        gap: '0.25rem',
        padding: '0.125rem',
        backgroundColor: 'rgba(15, 23, 42, 0.52)',
        borderRadius: '0.5rem'
      }}
    >
      {(['library', 'playlists'] as LibrarySurface[]).map(surface => {
        const isSelected = activeSurface === surface;
        return (
          <button
            key={surface}
            type="button"
            role="tab"
            aria-label={surface === 'library' ? 'Library' : 'Playlists'}
            aria-selected={isSelected}
            aria-controls={`${surface}-surface`}
            title={surface === 'library' ? 'Library' : 'Playlists'}
            onClick={() => handleSelectSurface(surface)}
            style={{
              position: 'relative',
              width: LIBRARY_HEADER_CONTROL_SIZE,
              height: LIBRARY_HEADER_TAB_HEIGHT,
              flex: `0 0 ${LIBRARY_HEADER_CONTROL_SIZE}`,
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isSelected ? '#f9fafb' : '#9ca3af',
              backgroundColor: isSelected ? '#334155' : 'transparent',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              transition: 'color 160ms ease, background-color 160ms ease'
            }}
          >
            {surface === 'library' ? (
              <LibraryIcon size={21} strokeWidth={1.9} aria-hidden="true" />
            ) : (
              <>
                <FolderOpen size={22} strokeWidth={1.9} aria-hidden="true" />
                <Play
                  size={11}
                  fill="currentColor"
                  strokeWidth={2.5}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    right: '0.4rem',
                    bottom: '0.32rem'
                  }}
                />
              </>
            )}
          </button>
        );
      })}
    </div>
  );

  const searchControl = (
    <div
      ref={searchContainerRef}
      aria-expanded={isSearchExpanded}
      style={{
        position: 'relative',
        width: isSearchExpanded ? LIBRARY_HEADER_SEARCH_WIDTH : LIBRARY_HEADER_CONTROL_SIZE,
        transition: 'width 180ms ease, flex-basis 180ms ease',
        flex: isSearchExpanded ? '1 1 11rem' : `0 0 ${LIBRARY_HEADER_CONTROL_SIZE}`,
        minWidth: isSearchExpanded ? LIBRARY_HEADER_SEARCH_MIN_WIDTH : LIBRARY_HEADER_CONTROL_SIZE,
        maxWidth: isSearchExpanded ? LIBRARY_HEADER_SEARCH_WIDTH : LIBRARY_HEADER_CONTROL_SIZE
      }}
    >
      {!isSearchExpanded ? (
        <button
          type="button"
          aria-label={`Open ${searchContentLabel} search`}
          aria-expanded={false}
          title={`Search ${searchContentLabel}`}
          onClick={() => setSearchOpen(true)}
          style={{
            ...toolbarIconButtonStyle
          }}
        >
          <Search size={18} aria-hidden="true" />
        </button>
      ) : (
        <>
          <Search
            size={18}
            aria-hidden="true"
            style={{ position: 'absolute', left: '0.7rem', top: '0.62rem', color: '#9ca3af' }}
          />
          <input
            ref={searchInputRef}
            id="library-title-search"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleCloseSearch();
            }}
            placeholder={isPlaylistCollectionView ? 'Search playlists' : 'Search videos'}
            aria-label={isPlaylistCollectionView ? 'Search playlists' : 'Search library'}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              height: LIBRARY_HEADER_CONTROL_SIZE,
              padding: '0.45rem 2.2rem 0.45rem 2.2rem',
              color: 'white',
              backgroundColor: '#111827',
              border: '1px solid #4b5563',
              borderRadius: '0.5rem',
              outline: 'none',
              fontSize: '0.875rem'
            }}
          />
          <button
            type="button"
            aria-label={`Close ${searchContentLabel} search`}
            onClick={handleCloseSearch}
            style={{
              position: 'absolute',
              top: '0.125rem',
              right: '0.125rem',
              width: '2.0625rem',
              height: '2.0625rem',
              padding: 0,
              color: '#9ca3af',
              background: 'transparent',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={17} aria-hidden="true" />
          </button>
        </>
      )}
    </div>
  );

  const filterToggleControl = (
    <button
      type="button"
      aria-label="Toggle library filters"
      aria-expanded={!isPlaylistCollectionView && filtersOpen}
      aria-controls="library-filter-controls"
      title={isPlaylistCollectionView ? 'Filters unavailable for playlist list' : 'Settings'}
      disabled={isPlaylistCollectionView}
      onClick={() => {
        if (!isPlaylistCollectionView) {
          setFiltersOpen(prev => !prev);
        }
      }}
      style={{
        ...toolbarIconButtonStyle,
        color: isPlaylistCollectionView ? '#64748b' : activeFilterCount > 0 ? '#dbeafe' : '#9ca3af',
        backgroundColor: 'transparent',
        border: '1px solid transparent',
        cursor: isPlaylistCollectionView ? 'not-allowed' : 'pointer',
        opacity: isPlaylistCollectionView ? 0.55 : 1
      }}
    >
      <SlidersHorizontal size={18} aria-hidden="true" />
    </button>
  );

  const createPlaylistControl = (
    <button
      type="button"
      aria-label="Create playlist"
      title="Create playlist"
      disabled={creatingPlaylist}
      onClick={handleCreatePlaylist}
      style={{
        ...toolbarIconButtonStyle,
        color: creatingPlaylist ? '#6b7280' : '#d1d5db',
        cursor: creatingPlaylist ? 'not-allowed' : 'pointer',
        opacity: creatingPlaylist ? 0.6 : 1
      }}
    >
      <Plus size={19} strokeWidth={2} aria-hidden="true" />
    </button>
  );

  const headerActionControl = isPlaylistCollectionView
    ? createPlaylistControl
    : filterToggleControl;

  const showContextControls = (
    (!isPlaylistCollectionView && filtersOpen) ||
    isSelectionMode
  );

  // Handle share revocation
  const handleRevokeShare = async (video: NormalizedLibraryProject) => {
    if (!sharingEnabled) return;
    const share = shareDataMap[video.id];
    if (!share || !userProfile) return;

    if (!window.confirm('Are you sure you want to revoke this share link? This cannot be undone.')) {
      return;
    }

    setLoadingShares(prev => ({ ...prev, [video.id]: true }));

    try {
      await deactivateShare(share.shareId, 'user_revoked');

      setShareDataMap(prev => ({ ...prev, [video.id]: null }));

      await clearLibraryProjectShare(video);

      console.log('🟢 Share revoked successfully');
    } catch (error) {
      console.error('Failed to revoke share:', error);
      setError(error instanceof Error ? error.message : 'Failed to revoke share');
    } finally {
      setLoadingShares(prev => ({ ...prev, [video.id]: false }));
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={onClose}
        style={{
          position: 'fixed',
          left: '1rem',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '48px',
          height: '48px',
          backgroundColor: '#1f2937',
          color: 'white',
          border: 'none',
          borderRadius: '0.75rem',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          zIndex: 1000,
          transition: 'all 0.2s',
          fontSize: '1.25rem'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#374151';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#1f2937';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        <LibraryIcon size={22} strokeWidth={1.8} />
      </button>
    );
  }

  return (
    <>
      {/* Overlay backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999,
          animation: 'fadeIn 0.3s ease-in-out'
        }}
        onClick={onClose}
      />

      {/* Sliding Sidebar */}
      <div style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: 'min(420px, 100vw)',
        height: '100vh',
        backgroundColor: '#1f2937',
        color: 'white',
        boxShadow: '4px 0 24px rgba(0, 0, 0, 0.4)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        animation: 'slideIn 0.3s ease-out',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        {/* Header */}
        <div
          data-testid="library-toolbar-header"
          style={{
            position: 'relative',
            padding: '0.75rem 1rem',
            borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
            background: 'linear-gradient(180deg, rgba(17, 24, 39, 0.98) 0%, rgba(31, 41, 55, 0.96) 100%)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            minWidth: 0
          }}
        >
            {userProfile ? librarySurfaceSwitcher : (
              <LibraryIcon size={22} color="#cbd5e1" aria-hidden="true" />
            )}
            <h2
              aria-hidden={isSearchExpanded}
              style={{
                flex: isSearchExpanded ? '0 1 0' : '1 1 auto',
                width: isSearchExpanded ? 0 : undefined,
                minWidth: 0,
                maxWidth: isSearchExpanded ? 0 : '100%',
                margin: 0,
                padding: isSearchExpanded ? 0 : '0 0.5rem',
                color: '#f8fafc',
                fontSize: '1.125rem',
                lineHeight: 1.12,
                fontWeight: 500,
                letterSpacing: 0,
                textAlign: 'center',
                overflow: 'hidden',
                opacity: isSearchExpanded ? 0 : 1,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                transition: 'opacity 120ms ease, max-width 180ms ease, padding 180ms ease'
              }}
            >
            {headerTitle}
          </h2>
            <div
              style={{
                flex: isSearchExpanded ? '1 1 auto' : '0 1 auto',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '0.5rem'
            }}
          >
            {searchControl}
            {headerActionControl}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close library"
              title="Close"
              style={{
                ...toolbarIconButtonStyle,
                color: '#94a3b8',
                backgroundColor: 'transparent',
                border: '1px solid transparent'
              }}
            >
              <X size={22} strokeWidth={1.8} aria-hidden="true" />
            </button>
          </div>
        </div>

        {!userProfile && (
          <div
            role="note"
            style={{
              padding: '0.55rem 1rem',
              color: '#bfdbfe',
              backgroundColor: 'rgba(30, 64, 175, 0.22)',
              borderBottom: '1px solid rgba(96, 165, 250, 0.2)',
              fontSize: '0.75rem',
              lineHeight: 1.4
            }}
          >
            Saved on this device. Sign in to back up your library and open it on other devices.
          </div>
        )}

        <div
          data-testid="library-progress-lane"
          role="status"
          aria-label={progressAriaLabel}
          aria-live="polite"
          style={{
            padding: '0.35rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.625rem',
            color: '#cbd5e1',
            backgroundColor: 'rgba(15, 23, 42, 0.24)',
            borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: 0
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: '3.25rem',
              flex: '0 0 3.25rem',
              textAlign: 'left',
              color: '#94a3b8'
            }}
          >
            {progressLabel}
          </span>
          <span
            aria-hidden="true"
            style={{
              position: 'relative',
              flex: '1 1 auto',
              height: '0.1875rem',
              overflow: 'hidden',
              borderRadius: '999px',
              backgroundColor: 'rgba(148, 163, 184, 0.16)'
            }}
          >
            <span
              style={{
                position: 'absolute',
                inset: 0,
                width: `${progressRatio * 100}%`,
                borderRadius: '999px',
                backgroundColor: 'rgba(148, 163, 184, 0.42)'
              }}
            />
          </span>
        </div>

        {showContextControls && (
          <div style={{
            padding: '0.5rem 1rem 0.625rem',
            borderBottom: '1px solid #374151',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}>
          {!isPlaylistCollectionView && filtersOpen && (
            <div
              id="library-filter-controls"
              style={{
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >
            <div
              role="group"
              aria-label="Library source filter"
              style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap'
              }}
            >
              {SOURCE_FILTER_OPTIONS.map(option => {
                const isSelected = sourceFilter === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-label={option.ariaLabel}
                    aria-pressed={isSelected}
                    onClick={() => {
                      setSourceFilter(prev => prev === option.value ? 'all' : option.value);
                    }}
                    style={{
                      padding: '0.375rem 0.75rem',
                      color: isSelected ? 'white' : '#d1d5db',
                      backgroundColor: isSelected ? '#2563eb' : '#111827',
                      border: `1px solid ${isSelected ? '#60a5fa' : '#4b5563'}`,
                      borderRadius: '999px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      transition: 'all 0.2s'
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <div
              role="group"
              aria-label="Library learning status filter"
              style={{
                display: 'flex',
                gap: '0.375rem',
                flexWrap: 'wrap'
              }}
            >
              {LEARNING_STATUS_FILTER_OPTIONS.map(option => {
                const isSelected = statusFilter === option.value;
                const statusColor = getLearningStatusFilterColor(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-label={option.ariaLabel}
                    aria-pressed={isSelected}
                    title={option.label}
                    onClick={() => {
                      setStatusFilter(prev => prev === option.value ? 'all' : option.value);
                    }}
                    style={{
                      width: '2rem',
                      height: '2rem',
                      padding: 0,
                      color: isSelected ? statusColor : '#d1d5db',
                      backgroundColor: isSelected ? 'rgba(31, 41, 55, 0.95)' : '#111827',
                      border: `1px solid ${isSelected ? statusColor : '#4b5563'}`,
                      borderRadius: '999px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                  >
                    {renderLearningStatusIcon(option.value)}
                  </button>
                );
              })}
            </div>
            </div>
          )}
          {isSelectionMode && (
            <div
              data-testid="library-selection-toolbar"
              role="toolbar"
              aria-label="Selection actions"
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'nowrap',
                gap: '0.375rem',
                minWidth: 0,
                width: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
                minHeight: '2.25rem',
                padding: '0.375rem 0.5rem',
                backgroundColor: 'rgba(15, 23, 42, 0.74)',
                border: '1px solid rgba(148, 163, 184, 0.22)',
                borderRadius: '0.5rem'
              }}
            >
              <span
                aria-live="polite"
                aria-label={`${selectedActionCount} ${selectedActionNoun}${selectedActionCount === 1 ? '' : 's'} selected`}
                style={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  color: '#e5e7eb',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {selectedActionCount} {selectedActionNoun}{selectedActionCount === 1 ? '' : 's'}
              </span>
              <button
                type="button"
                aria-label="Cancel selection"
                title="Cancel selection"
                onClick={handleCancelSelection}
                disabled={bulkActionInProgress}
                style={{
                  width: '2rem',
                  height: '2rem',
                  flex: '0 0 2rem',
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#cbd5e1',
                  background: 'transparent',
                  border: '1px solid #334155',
                  borderRadius: '0.375rem',
                  cursor: bulkActionInProgress ? 'not-allowed' : 'pointer',
                  opacity: bulkActionInProgress ? 0.55 : 1
                }}
              >
                <X size={14} />
              </button>
              {selectedPlaylistForRename && (
                <button
                  type="button"
                  aria-label={`Rename selected playlist ${selectedPlaylistForRename.title}`}
                  title="Rename"
                  disabled={bulkActionInProgress}
                  onClick={() => handleStartPlaylistRename(selectedPlaylistForRename)}
                  style={{
                    width: '2rem',
                    flex: '0 0 2rem',
                    height: '2rem',
                    padding: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: bulkActionInProgress ? '#64748b' : '#dbeafe',
                    backgroundColor: bulkActionInProgress ? 'rgba(31, 41, 55, 0.7)' : 'rgba(37, 99, 235, 0.18)',
                    border: '1px solid rgba(96, 165, 250, 0.35)',
                    borderRadius: '0.375rem',
                    cursor: bulkActionInProgress ? 'not-allowed' : 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '700'
                  }}
                >
                  <Pencil size={14} aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                aria-label={
                  selection?.kind === 'playlist-videos'
                    ? `Remove ${selectedActionCount} selected ${selectedActionNoun}${selectedActionCount === 1 ? '' : 's'} from playlist`
                    : `Delete ${selectedActionCount} selected ${selectedActionNoun}${selectedActionCount === 1 ? '' : 's'}`
                }
                title={selection?.kind === 'playlist-videos' ? 'Remove from playlist' : 'Delete'}
                disabled={bulkActionInProgress || selectedActionCount === 0}
                onClick={() => {
                  if (selection?.kind === 'playlists') {
                    void handleBulkDeletePlaylists();
                  } else if (selection?.kind === 'playlist-videos') {
                    void handleBulkRemoveFromPlaylist();
                  } else {
                    void handleBulkDeleteVideos();
                  }
                }}
                style={{
                  width: '2rem',
                  flex: '0 0 2rem',
                  height: '2rem',
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: bulkActionInProgress || selectedActionCount === 0
                    ? '#64748b'
                    : selection?.kind === 'playlist-videos' ? '#bfdbfe' : '#fecaca',
                  backgroundColor: bulkActionInProgress || selectedActionCount === 0
                    ? 'rgba(31, 41, 55, 0.7)'
                    : selection?.kind === 'playlist-videos'
                      ? 'rgba(37, 99, 235, 0.18)'
                      : 'rgba(127, 29, 29, 0.42)',
                  border: `1px solid ${selection?.kind === 'playlist-videos' ? 'rgba(96, 165, 250, 0.35)' : 'rgba(248, 113, 113, 0.35)'}`,
                  borderRadius: '0.375rem',
                  cursor: bulkActionInProgress || selectedActionCount === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: '700'
                }}
              >
                {selection?.kind === 'playlist-videos' ? <MinusCircle size={14} /> : <Trash2 size={14} />}
              </button>
            </div>
          )}
        </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            margin: '1rem',
            padding: '0.75rem',
            backgroundColor: '#7f1d1d',
            color: '#fca5a5',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Video List */}
        <div
          ref={listScrollRef}
          onScroll={updateViewportProgress}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '0.75rem 1rem 1rem'
          }}
        >
          {activeSurface === 'playlists' && !activePlaylist ? (
            <div id="playlists-surface" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {filteredPlaylists.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6b7280' }}>
                  <FolderOpen size={42} style={{ margin: '0 auto 0.75rem', opacity: 0.3 }} />
                  <p style={{ fontSize: '1rem', fontWeight: '500', margin: 0, color: '#9ca3af' }}>
                    {searchQuery.trim() ? 'No matching playlists' : 'No playlists yet'}
                  </p>
                </div>
              ) : filteredPlaylists.map(playlist => {
                const isPlaylistSelected = selectedIdSet.has(playlist.id);
                const playlistItems = playlistMemberships[playlist.id] || [];
                const coverStackItems = playlistCoverItems[playlist.id] || [];
                const frontCoverItem = coverStackItems[0] || null;
                const isEditingPlaylist = editingPlaylistId === playlist.id;
                return (
                  <div
                    key={playlist.id}
                    data-testid={`playlist-card-${playlist.id}`}
                    data-library-list-item="true"
                    role="button"
                    tabIndex={0}
                    aria-label={isSelectionMode
                      ? `${isPlaylistSelected ? 'Deselect' : 'Select'} playlist ${playlist.title}`
                      : `Show playlist ${playlist.title}`}
                    aria-pressed={isSelectionMode ? isPlaylistSelected : undefined}
                    onPointerDown={event => handleSelectionLongPressStart(event, 'playlists', playlist.id)}
                    onPointerMove={handleSelectionPointerMove}
                    onPointerUp={clearLongPressSelectionTimer}
                    onPointerCancel={clearLongPressSelectionTimer}
                    onPointerLeave={clearLongPressSelectionTimer}
                    onContextMenu={event => {
                      if (suppressNextCardClickRef.current || isSelectionMode || isPlaylistSelected) {
                        event.preventDefault();
                      }
                    }}
                    onKeyDown={event => handleSelectionCardKeyDown(
                      event,
                      'playlists',
                      playlist.id,
                      () => handleSelectPlaylist(playlist.id)
                    )}
                    onClick={(event) => {
                      if (shouldSuppressCardClickAfterLongPress(event)) return;
                      if (isEditingPlaylist) return;
                      if (isSelectionMode) {
                        handleToggleSelection('playlists', playlist.id);
                      } else {
                        handleSelectPlaylist(playlist.id);
                      }
                    }}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.625rem',
                      padding: '0.625rem',
                      boxSizing: 'border-box',
                      color: '#e5e7eb',
                      backgroundColor: isPlaylistSelected ? 'rgba(30, 64, 175, 0.32)' : '#111827',
                      border: `1px solid ${isPlaylistSelected ? '#60a5fa' : '#374151'}`,
                      borderRadius: '0.75rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      userSelect: 'none',
                      touchAction: 'manipulation'
                    }}
                  >
                    {coverStackItems.length > 0 ? (
                      <div
                        data-testid={`playlist-cover-collage-${playlist.id}`}
                        style={{
                          position: 'relative',
                          width: 'calc(100% - 1rem)',
                          maxWidth: '100%',
                          aspectRatio: '16 / 9',
                          marginRight: '1rem',
                          marginBottom: '0.875rem',
                          flex: '0 0 auto',
                          display: 'grid',
                          gap: '2px',
                          boxSizing: 'border-box',
                          overflow: 'hidden',
                          borderRadius: '0.45rem',
                          backgroundColor: 'transparent',
                          ...getPlaylistCoverGridTemplate(coverStackItems.length)
                        }}
                      >
                        {coverStackItems.map((coverItem, index) => (
                          <img
                            key={getLibraryProjectMembershipKey(coverItem)}
                            src={thumbnailUrls[getLibraryProjectMembershipKey(coverItem)] || generatePlaceholderThumbnail(coverItem.title)}
                            alt={index === 0 ? `${coverItem.title} cover` : ''}
                            aria-hidden={index === 0 ? undefined : 'true'}
                            onError={(event) => {
                              if (event.currentTarget.src.startsWith('data:')) return;
                              setThumbnailUrls(prev => ({
                                ...prev,
                                [getLibraryProjectMembershipKey(coverItem)]: generatePlaceholderThumbnail(coverItem.title)
                              }));
                            }}
                            style={{
                              display: 'block',
                              width: '100%',
                              height: '100%',
                              boxSizing: 'border-box',
                              objectFit: 'cover',
                              backgroundColor: 'transparent',
                              ...getPlaylistCoverGridItemStyle(coverStackItems.length, index)
                            }}
                            loading="lazy"
                          />
                        ))}
                        <span
                          aria-hidden="true"
                          style={{
                            position: 'absolute',
                            inset: 'auto 0 0 0',
                            height: '45%',
                            background: 'linear-gradient(to top, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0))',
                            pointerEvents: 'none'
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        aria-hidden="true"
                        style={{
                          width: 'calc(100% - 0.875rem)',
                          aspectRatio: '16 / 9',
                          marginRight: '0.875rem',
                          marginBottom: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#8b5cf6',
                          backgroundColor: '#1e1b4b',
                          border: '1px solid rgba(139, 92, 246, 0.22)',
                          borderRadius: '0.45rem'
                        }}
                      >
                        <FolderOpen size={34} />
                      </div>
                    )}
                    <span style={{ minWidth: 0, height: '2.625rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', justifyContent: 'center' }}>
                      {(
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', minWidth: 0 }}>
                          <strong style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.9375rem', fontWeight: '650' }}>
                            {playlist.title}
                          </strong>
                          <span style={{ flex: '0 0 auto', color: '#9ca3af', fontSize: '0.6875rem' }}>
                            {playlistItems.length} items
                          </span>
                        </span>
                      )}
                      <span
                        aria-hidden={Boolean(frontCoverItem)}
                        style={{
                          height: '0.8125rem',
                          color: '#64748b',
                          fontSize: '0.6875rem',
                          lineHeight: 1,
                          visibility: frontCoverItem ? 'hidden' : 'visible'
                        }}
                      >
                        {frontCoverItem ? 'Reserved' : 'No items yet'}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          ) : loading && videos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6b7280' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '4px solid #374151', 
                borderTop: '4px solid #2563eb',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 1rem'
              }} />
              Loading your videos...
            </div>
          ) : videos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6b7280' }}>
              <Video size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
              <p style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.5rem', color: '#9ca3af' }}>
                {error ? 'Library items could not be loaded' : 'No library items yet'}
              </p>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                {error ? 'Check Firebase rules for projects and videos.' : 'Save your first video to start building your library'}
              </p>
            </div>
          ) : activePlaylist && scopedVideos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6b7280' }}>
              <Video size={36} style={{ margin: '0 auto 0.75rem', opacity: 0.3 }} />
              <p style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.5rem', color: '#9ca3af' }}>
                No videos in this playlist
              </p>
            </div>
          ) : filteredVideos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6b7280' }}>
              <Video size={36} style={{ margin: '0 auto 0.75rem', opacity: 0.3 }} />
              <p style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.5rem', color: '#9ca3af' }}>
                No matching videos
              </p>
              {hasActiveLibraryFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  style={{
                    marginTop: '0.25rem',
                    padding: '0.45rem 0.7rem',
                    color: '#bfdbfe',
                    background: 'transparent',
                    border: '1px solid rgba(96, 165, 250, 0.35)',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '650'
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div
              data-testid={activePlaylist ? 'playlist-detail-videos' : undefined}
              style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
            >
  {filteredVideos.map(video => {
  // Calculate share info for this video
  const isYouTube = isYouTubeLibraryProject(video);
  const mediaAvailability = getLibraryProjectMediaAvailability(video);
  const share = sharingEnabled ? shareDataMap[video.id] : null;
  const viewsRemaining = share ? calculateViewsRemaining(share.accessCount, share.maxViews) : 0;
  const daysRemaining = share ? calculateDaysRemaining(share.expiresAt) : 0;
  const timeFormatted = share ? formatTimeRemaining(share.expiresAt) : '';
  const shareStatus = share ? getShareStatus(share) : 'none';
  const isNearLimit = share && (viewsRemaining <= 5 || daysRemaining <= 1);
	  const learningStatusLabel = getLearningStatusLabel(video.learningStatus);
	  const isUpdatingLearningStatus = updatingStatusId === video.id;
	  const isUpdatingPlaylist = updatingPlaylistId === video.id;
	  const isPlaylistPickerOpen = openPlaylistPickerId === video.id;
	  const isVideoMenuOpen = openVideoMenuId === video.id;
	  const videoItemKey = getLibraryProjectMembershipKey(video);
	  const isVideoSelected = selectedIdSet.has(videoItemKey);
	  const learningStatusColor = video.learningStatus
	    ? getLearningStatusFilterColor(video.learningStatus)
	    : '#64748b';
	  const playlistItemIndex = activePlaylistMembershipIndexByItem.get(videoItemKey) ?? -1;
	  const canReorderVideo = canReorderPlaylist && playlistItemIndex >= 0;

  return (
    <div
      key={videoItemKey}
      data-testid={`playlist-video-card-${videoItemKey}`}
      data-library-list-item="true"
      role="button"
      tabIndex={0}
      aria-label={isSelectionMode
        ? `${isVideoSelected ? 'Deselect' : 'Select'} video ${video.title}`
        : `Open video ${video.title}`}
      aria-pressed={isSelectionMode ? isVideoSelected : undefined}
      draggable={canReorderVideo}
      style={{
        position: 'relative',
        backgroundColor: isVideoSelected ? 'rgba(30, 64, 175, 0.28)' : hoveredVideo === video.id ? '#374151' : '#111827',
        border: `1px solid ${isVideoSelected ? '#60a5fa' : 'transparent'}`,
        boxSizing: 'border-box',
        borderRadius: '0.75rem',
        overflow: 'hidden',
        transition: 'all 0.2s',
        cursor: canReorderVideo ? 'grab' : 'pointer',
        outline: dragOverPlaylistItemKey === videoItemKey ? '2px solid #60a5fa' : 'none',
        opacity: draggedPlaylistItemKey === videoItemKey ? 0.65 : 1,
        userSelect: 'none',
        touchAction: 'manipulation'
      }}
      onMouseEnter={() => setHoveredVideo(video.id)}
      onMouseLeave={() => setHoveredVideo(null)}
      onPointerDown={event => handleSelectionLongPressStart(event, selectionContext.kind, videoItemKey)}
      onPointerMove={handleSelectionPointerMove}
      onPointerUp={clearLongPressSelectionTimer}
      onPointerCancel={clearLongPressSelectionTimer}
      onPointerLeave={clearLongPressSelectionTimer}
      onContextMenu={event => {
        if (suppressNextCardClickRef.current || isSelectionMode || isVideoSelected) {
          event.preventDefault();
        }
      }}
      onKeyDown={event => handleSelectionCardKeyDown(
        event,
        selectionContext.kind,
        videoItemKey,
        () => handleLoadVideo(video)
      )}
      onDragStart={event => handlePlaylistDragStart(event, video)}
      onDragOver={event => handlePlaylistDragOver(event, video)}
      onDrop={event => handlePlaylistDrop(event, video)}
      onDragEnd={handlePlaylistDragEnd}
      onClick={(event) => {
        if (shouldSuppressCardClickAfterLongPress(event)) return;
        if (isSelectionMode) {
          handleToggleSelection(selectionContext.kind, videoItemKey);
        } else {
          handleLoadVideo(video);
        }
      }}
    >
      {/* Thumbnail */}
      <div style={{
        position: 'relative',
        width: '100%',
        paddingBottom: '56.25%',
        backgroundColor: '#000',
        overflow: 'hidden'
      }}>
        {thumbnailsLoading[getLibraryProjectMembershipKey(video)] ? (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#1f2937'
          }}>
            <div style={{ 
              width: '30px', 
              height: '30px', 
              border: '3px solid #374151', 
              borderTop: '3px solid #60a5fa',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
          </div>
        ) : (
          <img
            src={thumbnailUrls[getLibraryProjectMembershipKey(video)] || generatePlaceholderThumbnail(video.title)}
            alt={video.title}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            loading="lazy"
            onLoad={(e) => {
              // YouTube returns a 200 with a 120x90 "video unavailable" placeholder
              // (deleted / private / region-blocked). Real hqdefault thumbnails are 480x360.
              // Detect the placeholder and swap to our generated thumb instead.
              if (!isYouTube) return;
              const img = e.currentTarget;
              if (img.naturalWidth > 0 && img.naturalWidth <= 120) {
                setThumbnailUrls(prev => (
                  prev[getLibraryProjectMembershipKey(video)]?.startsWith('data:')
                    ? prev
                    : { ...prev, [getLibraryProjectMembershipKey(video)]: generatePlaceholderThumbnail(video.title) }
                ));
              }
            }}
            onError={(e) => {
              // Network failure / blocked CDN — fall back to the generated placeholder.
              const img = e.currentTarget;
              if (!img.src.startsWith('data:')) {
                setThumbnailUrls(prev => (
                  prev[getLibraryProjectMembershipKey(video)]?.startsWith('data:')
                    ? prev
                    : { ...prev, [getLibraryProjectMembershipKey(video)]: generatePlaceholderThumbnail(video.title) }
                ));
              }
            }}
          />
        )}

        {isYouTube && (
          <div
            title="YouTube project - does not use upload storage"
            aria-label="YouTube project - does not use upload storage"
            style={{
              position: 'absolute',
              top: '0.75rem',
              left: '0.75rem',
              width: '2.25rem',
              height: '1.6rem',
              backgroundColor: '#ef4444',
              color: 'white',
              borderRadius: '0.35rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 6px 16px rgba(0, 0, 0, 0.35)',
              zIndex: 2
            }}
          >
            <Youtube size={20} strokeWidth={2.5} />
          </div>
        )}
        
        {/* Play overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: !isSelectionMode && hoveredVideo === video.id ? 1 : 0,
          transition: 'opacity 0.2s'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: 'rgba(37, 99, 235, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Play size={28} fill="white" color="white" />
          </div>
        </div>
        </div>

        {!isSelectionMode && <div style={{
          position: 'absolute',
          top: '0.625rem',
          right: '0.625rem',
          zIndex: 4
        }}>
          <button
            type="button"
            aria-label={`More actions for ${video.title}`}
            aria-expanded={isVideoMenuOpen}
            onClick={(event) => {
              event.stopPropagation();
              setOpenVideoMenuId(prev => prev === video.id ? null : video.id);
              setOpenPlaylistPickerId(null);
            }}
            style={{
              width: '2rem',
              height: '2rem',
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#e5e7eb',
              backgroundColor: 'rgba(15, 23, 42, 0.78)',
              border: '1px solid rgba(226, 232, 240, 0.22)',
              borderRadius: '0.375rem',
              cursor: 'pointer'
            }}
          >
            <MoreVertical size={16} />
          </button>
          {isVideoMenuOpen && (
            <div
              role="menu"
              aria-label={`Actions for ${video.title}`}
              onClick={event => event.stopPropagation()}
              style={{
                position: 'absolute',
                top: '2.25rem',
                right: 0,
                width: '10.5rem',
                padding: '0.25rem',
                backgroundColor: '#111827',
                border: '1px solid #4b5563',
                borderRadius: '0.5rem',
                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.4)'
              }}
            >
              <button
                type="button"
                role="menuitem"
                aria-label={`Learning status for ${video.title}: ${learningStatusLabel}`}
                disabled={isUpdatingLearningStatus}
                onClick={(event) => {
                  setOpenVideoMenuId(null);
                  handleCycleLearningStatus(event, video);
                }}
                style={{ ...menuItemStyle, opacity: isUpdatingLearningStatus ? 0.6 : 1 }}
              >
                Status: {learningStatusLabel}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpenVideoMenuId(null);
                  handleEditTitle(video);
                }}
                style={menuItemStyle}
              >
                Rename
              </button>
              {userProfile && !isLocalLibraryProject(video) && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpenVideoMenuId(null);
                    setOpenPlaylistPickerId(video.id);
                  }}
                  style={menuItemStyle}
                >
                  Manage playlists
                </button>
              )}
              {sharingEnabled && userProfile && !isYouTube && !isLocalLibraryProject(video) && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpenVideoMenuId(null);
                    if (shareDataMap[video.id]) {
                      handleRevokeShare(video);
                    } else {
                      handleCreateShare(video);
                    }
                  }}
                  disabled={loadingShares[video.id]}
                  style={menuItemStyle}
                >
                  {shareDataMap[video.id] ? 'Revoke share' : 'Share'}
                </button>
              )}
              {activePlaylistId && (
              <button
                type="button"
                role="menuitem"
                onClick={() => handleRemoveFromPlaylist(video, activePlaylistId)}
                aria-label="Remove from playlist"
                title="Remove from playlist"
                disabled={isUpdatingPlaylist}
                style={{ ...menuItemStyle, opacity: isUpdatingPlaylist ? 0.6 : 1 }}
              >
                  Remove from playlist
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpenVideoMenuId(null);
                  handleDeleteVideo(video.id);
                }}
                disabled={deletingId === video.id}
                style={{ ...menuItemStyle, color: '#fca5a5' }}
              >
                Delete video
              </button>
            </div>
          )}
        </div>}

      {/* Video Info */}
      <div style={{ padding: '1rem' }}>
        {/* Title - Editable */}
        {/* Renaming happens in the full-screen dialog rendered at the end of LibraryViewer. */}
        {(
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.5rem'
          }}>
            <h3 style={{
              flex: 1,
              margin: 0,
              fontSize: '1rem',
              fontWeight: '600',
              color: 'white',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {video.title}
            </h3>
            {!isSelectionMode && (
              <button
                type="button"
                aria-label={`Learning status for ${video.title}: ${learningStatusLabel}`}
                title={`Status: ${learningStatusLabel}`}
                disabled={isUpdatingLearningStatus}
                onClick={event => handleCycleLearningStatus(event, video)}
                style={{
                  width: '2rem',
                  height: '2rem',
                  flex: '0 0 2rem',
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: learningStatusColor,
                  backgroundColor: 'transparent',
                  border: '1px solid transparent',
                  borderRadius: '0.375rem',
                  cursor: isUpdatingLearningStatus ? 'not-allowed' : 'pointer',
                  opacity: isUpdatingLearningStatus ? 0.55 : 1
                }}
              >
                {renderLearningStatusIcon(video.learningStatus)}
              </button>
            )}
            {canReorderVideo && (
              <div
                role="group"
                aria-label={`Reorder ${video.title}`}
                onClick={event => event.stopPropagation()}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.125rem',
                  flex: '0 0 auto'
                }}
              >
                <span
                  role="img"
                  aria-label={`Drag to reorder ${video.title}`}
                  title="Drag to reorder"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: '#64748b',
                    padding: '0.25rem 0.125rem'
                  }}
                >
                  <GripVertical size={15} />
                </span>
                <button
                  type="button"
                  aria-label={`Move ${video.title} up`}
                  title="Move up"
                  disabled={reorderingPlaylist || playlistItemIndex === 0}
                  onClick={event => {
                    event.stopPropagation();
                    handleMovePlaylistItem(video, -1);
                  }}
                  style={{
                    width: '1.75rem',
                    height: '1.75rem',
                    padding: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#94a3b8',
                    background: 'transparent',
                    border: '1px solid #334155',
                    borderRadius: '0.3rem',
                    cursor: reorderingPlaylist || playlistItemIndex === 0 ? 'not-allowed' : 'pointer',
                    opacity: reorderingPlaylist || playlistItemIndex === 0 ? 0.45 : 1
                  }}
                >
                  <ArrowUp size={13} />
                </button>
                <button
                  type="button"
                  aria-label={`Move ${video.title} down`}
                  title="Move down"
                  disabled={reorderingPlaylist || playlistItemIndex === activePlaylistMemberships.length - 1}
                  onClick={event => {
                    event.stopPropagation();
                    handleMovePlaylistItem(video, 1);
                  }}
                  style={{
                    width: '1.75rem',
                    height: '1.75rem',
                    padding: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#94a3b8',
                    background: 'transparent',
                    border: '1px solid #334155',
                    borderRadius: '0.3rem',
                    cursor: reorderingPlaylist || playlistItemIndex === activePlaylistMemberships.length - 1 ? 'not-allowed' : 'pointer',
                    opacity: reorderingPlaylist || playlistItemIndex === activePlaylistMemberships.length - 1 ? 0.45 : 1
                  }}
                >
                  <ArrowDown size={13} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Share Status Badge - MOVED HERE (after title, before meta info) */}
        {share && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 0.75rem',
            backgroundColor: shareStatus === 'near_limit' ? '#fef3c7' : '#d1fae5',
            borderRadius: '0.5rem',
            marginBottom: '0.75rem',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            <Share2 size={12} color={shareStatus === 'near_limit' ? '#f59e0b' : '#10b981'} />
            <span style={{ color: shareStatus === 'near_limit' ? '#92400e' : '#065f46' }}>
              Shared
            </span>
            <span style={{
              marginLeft: '0.25rem',
              padding: '0.125rem 0.375rem',
              backgroundColor: 'white',
              borderRadius: '0.25rem',
              color: shareStatus === 'near_limit' ? '#f59e0b' : '#10b981',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              <Eye size={10} />
              {viewsRemaining}/{share.maxViews}
            </span>
            <span style={{
              padding: '0.125rem 0.375rem',
              backgroundColor: 'white',
              borderRadius: '0.25rem',
              color: shareStatus === 'near_limit' ? '#f59e0b' : '#10b981',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              <Calendar size={10} />
              {daysRemaining}d
            </span>
          </div>
        )}

        {/* Meta info */}
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          fontSize: '0.75rem',
          color: '#9ca3af',
          marginBottom: '0.75rem',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            flex: 1,
            opacity: (hoveredVideo === video.id || editingTitle === video.id) ? 0 : 1,
            transition: 'opacity 0.2s'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Clock size={12} />
              {formatDate(video.createdAt)}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              {isYouTube && <Youtube size={12} color="#f87171" />}
              {mediaAvailability.status === 'cloud'
                ? formatFileSize(getLibraryProjectSize(video))
                : mediaAvailability.label}
            </span>
            <span style={{
              padding: '0.125rem 0.375rem',
              backgroundColor: '#1e3a8a',
              color: '#93c5fd',
              borderRadius: '0.25rem',
              fontWeight: '600'
            }}>
              {video.segments.length} seg
            </span>
          </div>

          {!isSelectionMode && isPlaylistPickerOpen && (
            <LibraryPlaylistPicker
              playlists={playlists}
              currentPlaylistIds={currentPlaylistIdsByItem.get(getLibraryProjectMembershipKey(video)) || []}
              disabled={isUpdatingPlaylist || creatingPlaylist}
              onToggle={playlistId => handleSetPlaylist(video, playlistId)}
              onCreate={title => handleCreatePlaylistForVideo(video, title)}
            />
          )}
        </div>

        {/* Warning for near-expiry shares - AT THE END */}
        {share && isNearLimit && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.5rem',
            backgroundColor: '#fef3c7',
            border: '1px solid #fbbf24',
            borderRadius: '0.375rem',
            fontSize: '0.75rem',
            color: '#92400e',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <AlertCircle size={12} />
            <span>
              {viewsRemaining <= 5 && `Only ${viewsRemaining} views left. `}
              {daysRemaining <= 1 && `Expires ${timeFormatted}.`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
})}
 </div>
          )}
        </div>
      </div>

      {actionToast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            left: '1rem',
            bottom: '1rem',
            zIndex: 1002,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            maxWidth: 'min(388px, calc(100vw - 2rem))',
            padding: '0.625rem 0.75rem',
            color: '#e5e7eb',
            backgroundColor: '#111827',
            border: '1px solid #4b5563',
            borderRadius: '0.5rem',
            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.35)',
            fontSize: '0.75rem'
          }}
        >
          <span style={{ minWidth: 0, flex: 1 }}>{actionToast.message}</span>
          <button
            type="button"
            onClick={async () => {
              try {
                await actionToast.undo();
                setActionToast(null);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to undo action');
              }
            }}
            style={{
              padding: 0,
              color: '#93c5fd',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: '700'
            }}
          >
            Undo
          </button>
        </div>
      )}

      {/* Renaming a video or playlist: a full-screen dialog so typing on a phone can't tap a card. */}
      {editingVideoForRename && (
        <RenameDialog
          title="Rename video"
          value={newTitle}
          onChange={setNewTitle}
          onConfirm={() => void handleSaveTitle(editingVideoForRename.id)}
          onCancel={handleCancelEdit}
          inputLabel={`Title for ${editingVideoForRename.title}`}
          confirmLabel={`Save title for ${editingVideoForRename.title}`}
          error={error}
        />
      )}
      {editingPlaylistForRename && (
        <RenameDialog
          title="Rename playlist"
          value={newPlaylistTitle}
          onChange={setNewPlaylistTitle}
          onConfirm={() => void handleSavePlaylistTitle(editingPlaylistForRename.id)}
          onCancel={handleCancelPlaylistRename}
          inputLabel={`Playlist title for ${editingPlaylistForRename.title}`}
          confirmLabel={`Save playlist ${editingPlaylistForRename.title}`}
          busy={renamingPlaylistId === editingPlaylistForRename.id || !normalizePlaylistTitle(newPlaylistTitle)}
          error={error}
        />
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default LibraryViewer;
